import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { MultiSelect } from '@/components/ui/multiselect';
import { ColorPicker } from '@/components/ui/color-picker';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Settings as SettingsIcon, Settings2, Clock, Users, Zap, Calendar, Shield, Globe, Palette, Trash2, Search, Copy, RefreshCw, ChevronDown, ChevronUp, FileText, Eye, CalendarClock, Plug, ChevronRight, BarChart3, RotateCcw, Filter, Plus, Upload, X, ChevronsUpDown, Check, User, Link, AlertTriangle, Save, Loader2 } from 'lucide-react';
import { useScreenData } from '@/hooks/useScreenData';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useUser } from '@/contexts/UserContext';
import { Slider } from '@/components/ui/slider';
import { DEFAULT_SIGNAL_WEIGHTS, signalWeightsTotal } from '@/hooks/useSignalWeights';
import type { SignalWeights } from '@/hooks/useSignalWeights';
import { useTimezone } from '@/hooks/use-timezone';
import { CalendarView, ProjectStatusType, StatusConfig, BookingType, TabConfiguration } from '@/types';
import { toast } from 'sonner';
import { TIMEZONES } from '@/lib/timezones';
import { PROJECT_STATUSES } from '@/lib/statuses';
import { useState, useEffect, useRef, useMemo } from 'react';
import { BookingTypeSetup as BookingTypeV2 } from '@/components/settings/BookingTypeSetup';
import { generateDefaultFieldConfigurations, generateDefaultFieldOrder, generateDefaultTabConfigurations } from '@/utils/field-configurations';
import { Integrations } from './Integrations';
import { DataModeToggle } from '@/components/DataModeToggle';
import { bookingTypesApi, fieldConfigurationsApi, tabConfigurationsApi, calendarSyncApi } from '@/services/api';
import { UtilisationSlider } from '@/components/UtilisationSlider';
import { DEFAULT_UTILIZATION_TIERS } from '@/utils/utilization-labels';
import { BookingTypeSetup } from '@/components/settings/BookingTypeSetup';
import { useConflictResolutionSettings, RESOLUTION_OPTION_CONFIGS } from '@/hooks/useConflictResolutionSettings';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';

const parseBool = (val: unknown): boolean => {
  if (val === true || val === 'true' || val === 1 || val === '1') return true;
  return false;
};

const normalizeFieldConfig = (fc: any) => ({
  fieldName: fc.fieldName || fc.FieldName || fc.fieldname || '',
  defaultName: fc.defaultName || fc.DefaultName || fc.fieldName || fc.FieldName || fc.fieldname || '',
  customName: fc.customName || fc.CustomName || '',
  isVisible: fc.isVisible != null ? parseBool(fc.isVisible) : (fc.IsVisible != null ? parseBool(fc.IsVisible) : (fc.visible != null ? parseBool(fc.visible) : (fc.Visible != null ? parseBool(fc.Visible) : true))),
  isCompulsory: fc.isCompulsory != null ? parseBool(fc.isCompulsory) : (fc.IsCompulsory != null ? parseBool(fc.IsCompulsory) : (fc.compulsory != null ? parseBool(fc.compulsory) : (fc.Compulsory != null ? parseBool(fc.Compulsory) : false))),
  showTooltip: fc.showTooltip != null ? parseBool(fc.showTooltip) : (fc.ShowTooltip != null ? parseBool(fc.ShowTooltip) : (fc.tooltip != null ? parseBool(fc.tooltip) : (fc.Tooltip != null ? parseBool(fc.Tooltip) : false))),
  showOnCustomTab: fc.showOnCustomTab != null ? parseBool(fc.showOnCustomTab) : (fc.ShowOnCustomTab != null ? parseBool(fc.ShowOnCustomTab) : false),
  placeholder: fc.placeholder || fc.Placeholder || '',
});

const normalizeTabConfig = (tc: any) => ({
  tabName: tc.tabName || tc.TabName || tc.tabname || '',
  defaultName: tc.defaultName || tc.DefaultName || tc.tabName || tc.TabName || tc.tabname || '',
  customName: tc.customName || tc.CustomName || '',
  isVisible: tc.isVisible != null ? parseBool(tc.isVisible) : (tc.IsVisible != null ? parseBool(tc.IsVisible) : (tc.visible != null ? parseBool(tc.visible) : (tc.Visible != null ? parseBool(tc.Visible) : true))),
});

const parseAndNormalizeFieldConfigs = (raw: unknown) => {
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed.map(normalizeFieldConfig).filter((f: any) => f.fieldName);
  } catch { return null; }
};

const parseAndNormalizeTabConfigs = (raw: unknown) => {
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed.map(normalizeTabConfig).filter((t: any) => t.tabName);
  } catch { return null; }
};

// Visible hours preview strip — extracted to avoid IIFE in JSX
interface VisibleHoursPreviewProps {
  visStart: number;
  bizStart: number;
  bizEnd: number;
  visEnd: number;
}
const VisibleHoursPreview = ({ visStart, bizStart, bizEnd, visEnd }: VisibleHoursPreviewProps) => {
  const preBuffer = bizStart - visStart;
  const business = bizEnd - bizStart;
  const postBuffer = visEnd - bizEnd;
  return (
    <div className="rounded-md border bg-muted/30 p-3 flex items-center gap-2 text-xs overflow-hidden">
      <span className="text-muted-foreground shrink-0">Preview:</span>
      <div className="flex-1 flex items-center h-5 rounded overflow-hidden">
        {preBuffer > 0 && (
          <div
            className="h-full bg-muted/70 flex items-center justify-center text-[10px] text-muted-foreground font-medium"
            style={{ flex: preBuffer }}
          >
            {visStart}:00
          </div>
        )}
        <div
          className="h-full bg-primary/20 flex items-center justify-center text-[10px] text-primary font-semibold"
          style={{ flex: business }}
        >
          {bizStart}:00 - {bizEnd}:00
        </div>
        {postBuffer > 0 && (
          <div
            className="h-full bg-muted/70 flex items-center justify-center text-[10px] text-muted-foreground font-medium"
            style={{ flex: postBuffer }}
          >
            {visEnd}:00
          </div>
        )}
      </div>
    </div>
  );
};

export function Settings({ initialTab }: { initialTab?: string }) {
  const { settings, resources, bookings, updateSettings, currentMode, schedulerGroups, addSchedulerGroup, updateSchedulerGroup, saveSchedulerGroup, deleteSchedulerGroup } = useScreenData('settings');
  const { isLoading: isContextLoading, demoBookingTypeUsages, setDemoBookingTypeUsages } = useScheduler();
  const { user } = useUser();
  const { isEnabled: isResolutionEnabled, toggle: toggleResolutionOption, whatIfEnabled, toggleWhatIf } = useConflictResolutionSettings();
  const { 
    timezone: effectiveTimezone, 
    setUserTimezone, 
    clearUserTimezone, 
    hasUserOverride, 
    systemDefault 
  } = useTimezone();
  const [searchQuery, setSearchQuery] = useState('');

  // Calendar sync state (moved from Integrations page)
  const [calendarUrl, setCalendarUrl] = useState<string>('');
  const [isGeneratingUrl, setIsGeneratingUrl] = useState(false);
  const [isDeletingUrl, setIsDeletingUrl] = useState(false);
  const [isLoadingCalendarUrl, setIsLoadingCalendarUrl] = useState(false);
  const [isDeleteUrlDialogOpen, setIsDeleteUrlDialogOpen] = useState(false);
  
  // Ensure utilisationTiers is never empty - use defaults if not set
  const utilisationTiers = settings.utilisationTiers && settings.utilisationTiers.length > 0 
    ? settings.utilisationTiers 
    : DEFAULT_UTILIZATION_TIERS;
  
  // Debug logging for timezone state on mount and updates
  useEffect(() => {
    console.log('🔝 [TIMEZONE STATE] Settings loaded:', {
      mode: currentMode,
      defaultTimezone: settings.defaultTimezone,
      timelineDefaultTimezone: settings.timelineDefaultTimezone,
      availableTimezones: settings.availableTimezones,
      effectiveTimezone,
      systemDefault,
      hasUserOverride,
      userId: user?.id,
    });
  }, [settings.defaultTimezone, effectiveTimezone, systemDefault, hasUserOverride, currentMode, user?.id]);
  
  // Use the imported function for default tab configurations
  const getDefaultTabConfigurations = generateDefaultTabConfigurations;

  const [enrichedConfigs, setEnrichedConfigs] = useState<Record<string, { fieldConfigurations: BookingType['fieldConfigurations']; tabConfigurations: BookingType['tabConfigurations'] }>>({});
  
  // Migrate existing booking types to include showOnCustomTab property
  // Merge in enrichedConfigs fetched from the dedicated field/tab config API tables
  const migratedBookingTypes = settings.bookingTypes?.map(bt => {
    const btId = String(bt.id);
    const enriched = enrichedConfigs[btId];
    const fieldConfigs = enriched?.fieldConfigurations || bt.fieldConfigurations || generateDefaultFieldConfigurations();
    const tabConfigs = enriched?.tabConfigurations || bt.tabConfigurations || getDefaultTabConfigurations();
    
    const updatedFields = fieldConfigs.map(field => {
      if (field.showOnCustomTab === undefined) {
        return { ...field, showOnCustomTab: false };
      }
      return field;
    });
    
    return { 
      ...bt, 
      id: btId,
      fieldConfigurations: updatedFields,
      tabConfigurations: tabConfigs
    };
  }) || [];
  
  const [selectedBookingTypeId, setSelectedBookingTypeId] = useState<string>(
    String(migratedBookingTypes[0]?.id ?? '')
  );
  const [activeTab, setActiveTab] = useState(initialTab ?? 'settings');
  const [adminSubTab, setAdminSubTab] = useState('general');

  // Sync activeTab when sidebar navigation changes the initialTab prop
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  
  
  // Local state for business hours to only save on blur
  const [localBusinessHoursStart, setLocalBusinessHoursStart] = useState<string>('');
  const [localBusinessHoursEnd, setLocalBusinessHoursEnd] = useState<string>('');

  // Local state for visible hours to only save on blur
  const [localVisibleHoursStart, setLocalVisibleHoursStart] = useState<string>('');
  const [localVisibleHoursEnd, setLocalVisibleHoursEnd] = useState<string>('');

  // Initialize local business hours from settings
  useEffect(() => {
    setLocalBusinessHoursStart(formatTimeValue(settings.businessHoursStart));
    setLocalBusinessHoursEnd(formatTimeValue(settings.businessHoursEnd));
  }, [settings.businessHoursStart, settings.businessHoursEnd]);

  // Initialize local visible hours from settings
  useEffect(() => {
    setLocalVisibleHoursStart(formatTimeValue(settings.visibleHoursStart ?? 7));
    setLocalVisibleHoursEnd(formatTimeValue(settings.visibleHoursEnd ?? 19));
  }, [settings.visibleHoursStart, settings.visibleHoursEnd]);

    const [isLoadingBookingType, setIsLoadingBookingType] = useState(false);
  
    // Auto-select default booking type when settings.bookingTypes becomes available in Live mode
    useEffect(() => {
      if (currentMode !== 'live') return;
      const btList = settings.bookingTypes;
      if (!btList || btList.length === 0) return;
      if (selectedBookingTypeId && btList.find(bt => String(bt.id) === String(selectedBookingTypeId))) return;
      const defaultBt = btList.find(bt => bt.name?.toLowerCase() === 'project work') || btList[0];
      if (defaultBt) {
        setSelectedBookingTypeId(String(defaultBt.id));
      }
    }, [currentMode, settings.bookingTypes]);

    useEffect(() => {
      if (currentMode !== 'live' || !selectedBookingTypeId) return;
      const btId = Number(selectedBookingTypeId);
      if (isNaN(btId)) return;
      if (enrichedConfigs[selectedBookingTypeId]) return;

      setIsLoadingBookingType(true);
      Promise.all([
        fieldConfigurationsApi.getByBookingTypeId(btId).catch(() => []),
        tabConfigurationsApi.getByBookingTypeId(btId).catch(() => []),
      ]).then(([dbFieldConfigs, dbTabConfigs]) => {
        let fieldConfigurations = generateDefaultFieldConfigurations();
        let tabConfigurations = generateDefaultTabConfigurations();

        if (Array.isArray(dbFieldConfigs) && dbFieldConfigs.length > 0) {
          fieldConfigurations = dbFieldConfigs.map((fc: any) => {
            const rules = fc.validationRules || fc.ValidationRules;
            let extra: Record<string, unknown> = {};
            if (rules && typeof rules === 'string') { try { extra = JSON.parse(rules); } catch { /* ignore */ } }
            return {
              fieldName: fc.fieldName || fc.FieldName || '',
              defaultName: fc.fieldName || fc.FieldName || '',
              placeholder: fc.placeholder || fc.Placeholder || '',
              defaultPlaceholder: fc.defaultPlaceholder || fc.DefaultPlaceholder || fc.placeholder || fc.Placeholder || '',
              isVisible: fc.isVisible != null ? parseBool(fc.isVisible) : (fc.IsVisible != null ? parseBool(fc.IsVisible) : true),
              isRequired: fc.isRequired != null ? parseBool(fc.isRequired) : (fc.IsRequired != null ? parseBool(fc.IsRequired) : false),
              isCompulsory: fc.isCompulsory != null ? parseBool(fc.isCompulsory) : (fc.IsCompulsory != null ? parseBool(fc.IsCompulsory) : false),
              showTooltip: extra.showTooltip != null ? parseBool(extra.showTooltip) : false,
              showOnCustomTab: extra.showOnCustomTab != null ? parseBool(extra.showOnCustomTab) : false,
              customName: fc.customName || fc.CustomName || '',
            };
          });
        }
        if (Array.isArray(dbTabConfigs) && dbTabConfigs.length > 0) {
          tabConfigurations = dbTabConfigs.map((tc: any) => ({
            tabName: tc.tabName || tc.TabName || '',
            defaultName: tc.defaultName || tc.DefaultName || '',
            customName: tc.customName || tc.CustomName || '',
            isVisible: tc.isVisible != null ? parseBool(tc.isVisible) : (tc.IsVisible != null ? parseBool(tc.IsVisible) : true),
          }));
        }

        setEnrichedConfigs(prev => ({ ...prev, [selectedBookingTypeId]: { fieldConfigurations, tabConfigurations } }));
      }).catch(e => {
        console.error('Failed to fetch field/tab configs for booking type', btId, e);
      }).finally(() => {
        setIsLoadingBookingType(false);
      });
    }, [currentMode, selectedBookingTypeId, enrichedConfigs]);
  
    // Accordion state management - only one section can be open at a time per tab
  // Settings Tab sections
  const [openSettingsSection, setOpenSettingsSection] = useState<string | null>(null);
  
  // Admin Settings General tab sections
  const [openGeneralSection, setOpenGeneralSection] = useState<string | null>(null);
  
  // Admin Settings Status tab sections
  const [openStatusSection, setOpenStatusSection] = useState<string | null>(null);
  
  // Admin Settings Absence tab sections
  const [openAbsenceSection, setOpenAbsenceSection] = useState<string | null>(null);
  
  // Admin Settings Timezone tab sections
  const [openTimezoneSection, setOpenTimezoneSection] = useState<string | null>(null);
  
  
  // Helper functions for accordion behavior
  const handleSettingsSectionToggle = (section: string) => {
    setOpenSettingsSection(prev => prev === section ? null : section);
  };
  
  const handleGeneralSectionToggle = (section: string) => {
    setOpenGeneralSection(prev => prev === section ? null : section);
  };
  
  const handleStatusSectionToggle = (section: string) => {
    setOpenStatusSection(prev => prev === section ? null : section);
  };
  
  const handleAbsenceSectionToggle = (section: string) => {
    setOpenAbsenceSection(prev => prev === section ? null : section);
  };
  
  const handleTimezoneSectionToggle = (section: string) => {
    setOpenTimezoneSection(prev => prev === section ? null : section);
  };
  
  
  const isAdministrator = user.workspaceRole === 'Administrator';
  const selectedBookingType = migratedBookingTypes.find(bt => String(bt.id) === String(selectedBookingTypeId));



  const handleDefaultViewChange = (view: CalendarView) => {
    // Update both defaultCalendarView and timelineDefaultTimeRange to keep them in sync
    const timeRange = view.charAt(0).toUpperCase() + view.slice(1); // Capitalize first letter
    updateSettings({ 
      defaultCalendarView: view,
      timelineDefaultTimeRange: timeRange 
    });
    // Toast removed - dropdown selection provides visual feedback
  };

  const handleAbsenceToggle = (checked: boolean) => {
    if (!checked) {
      updateSettings({ showAbsence: checked, absenceLabelOverride: undefined });
    } else {
      updateSettings({ showAbsence: checked });
    }
    // Toast removed - toggle state provides visual feedback
  };

  const handleAbsenceLabelChange = (value: string) => {
    updateSettings({ absenceLabelOverride: value || undefined });
    // Toast removed - input value provides visual feedback
  };

  const handleAbsenceColorChange = (color: string) => {
    updateSettings({ absenceColor: color });
    // Toast removed - color picker provides visual feedback
  };

  const handlePublicHolidaysToggle = (checked: boolean) => {
    updateSettings({ showPublicHolidays: checked });
    // Toast removed - toggle state provides visual feedback
  };

  const handlePublicHolidayColorChange = (color: string) => {
    updateSettings({ publicHolidayColor: color });
    // Toast removed - color picker provides visual feedback
  };

  const handleGroupingMethodChange = (value: 'scheduler-group' | 'department') => {
    updateSettings({ groupingMethod: value });
    // Toast removed - dropdown selection provides visual feedback
  };

  const handleConfirmDragDropToggle = (checked: boolean) => {
    updateSettings({ confirmDragDrop: checked });
    // Toast removed - toggle state provides visual feedback
  };

  const handleDefaultToLoggedInResourceToggle = (checked: boolean) => {
    updateSettings({ defaultToLoggedInResource: checked });
    // Toast removed - toggle state provides visual feedback
  };

  // User Preference Handlers
  const handleUserConfirmDragDropToggle = (checked: boolean) => {
    updateSettings({ userConfirmDragDrop: checked });
    // Toast removed - toggle state provides visual feedback
  };

  const handleUserDefaultToLoggedInResourceToggle = (checked: boolean) => {
    updateSettings({ userDefaultToLoggedInResource: checked });
    // Toast removed - toggle state provides visual feedback
  };

  const handleShowResourceRatesToggle = (checked: boolean) => {
    if (!checked) {
      // When disabling, also disable copy resource rates
      updateSettings({ showResourceRates: checked, copyResourceRates: false });
    } else {
      updateSettings({ showResourceRates: checked });
    }
    // Toast removed - toggle state provides visual feedback
  };

  const handleCopyResourceRatesToggle = (checked: boolean) => {
    updateSettings({ copyResourceRates: checked });
    // Toast removed - toggle state provides visual feedback
  };

  const handleRagThresholdChange = (field: 'greenThreshold' | 'amberThreshold', value: string) => {
    const num = parseInt(value);
    if (isNaN(num) || num < 0 || num > 100) return;
    const current = settings.aiRagThresholds ?? { greenThreshold: 80, amberThreshold: 50 };
    updateSettings({ aiRagThresholds: { ...current, [field]: num } });
  };

  const handleResetRagThresholds = () => {
    updateSettings({ aiRagThresholds: { greenThreshold: 80, amberThreshold: 50 } });
    toast.success('Match thresholds reset to defaults');
  };

  // ── Signal Weights ─────────────────────────────────────────────────────────
  const currentWeights: SignalWeights = settings.aiSignalWeights ?? DEFAULT_SIGNAL_WEIGHTS;
  const weightsTotal = signalWeightsTotal(currentWeights);
  const weightsValid = Math.abs(weightsTotal - 100) <= 1;

  const handleSignalWeightChange = (key: keyof SignalWeights, value: number) => {
    updateSettings({ aiSignalWeights: { ...currentWeights, [key]: value } });
  };

  const handleResetSignalWeights = () => {
    updateSettings({ aiSignalWeights: { ...DEFAULT_SIGNAL_WEIGHTS } });
    toast.success('Signal weights reset to defaults');
  };

  const signalConfig: { key: keyof SignalWeights; label: string; description: string; icon: string }[] = [
    { key: 'availability', label: 'Availability', description: 'How free the resource is during the booking period', icon: '📅' },
    { key: 'utilisation', label: 'Utilisation', description: 'Current workload — prefers resources with capacity to spare', icon: '📊' },
    { key: 'role', label: 'Role match', description: 'How closely the resource\'s role matches what\'s required', icon: '💼' },
    { key: 'location', label: 'Location', description: 'Whether the resource is based at the required location', icon: '📍' },
    { key: 'skills', label: 'Skills', description: 'How many of the required skills the resource has', icon: '🎯' },
    { key: 'budget', label: 'Budget', description: 'Whether the resource\'s day rate fits within budget', icon: '💰' },
  ];

  const handleTimezonesToggle = (checked: boolean) => {
    updateSettings({ enableTimezones: checked });
    if (checked) {
      toast.success('Multiple timezones enabled', {
        description: 'Timezone settings have been restored. The timeline will now shift bookings based on the selected timezone.',
      });
    } else {
      toast.success('Multiple timezones disabled', {
        description: 'Timeline will display bookings in their stored timezone. Your timezone settings are preserved.',
      });
    }
  };

  const handleTimezonesChange = (timezones: string[]) => {
    updateSettings({ availableTimezones: timezones });
    // Toast removed - multiselect provides visual feedback
  };

  const handleDefaultTimezoneChange = async (value: string) => {
    try {
      if (hasUserOverride) {
        await clearUserTimezone();
      }
      await updateSettings({ defaultTimezone: value, timelineDefaultTimezone: value });
      await setUserTimezone(value);
      toast.success('System default timezone updated', {
        description: 'This timezone will be used for the timeline and all users.',
      });
    } catch (error) {
      toast.error('Failed to save timezone', {
        description: 'Please check your connection and try again.',
      });
    }
  };

  const handleQuickAddDefaultChange = (value: string) => {
    updateSettings({ quickAddDefault: value });
    // Toast removed - dropdown selection provides visual feedback
  };

  const handleDescriptionDefaultChange = (value: string) => {
    updateSettings({ descriptionDefault: value as 'none' | 'from-project' | 'from-project-description' });
    // Toast removed - dropdown selection provides visual feedback
  };

  const handleProjectStatusesChange = (values: string[]) => {
    if (values.includes('all')) {
      updateSettings({ availableProjectStatuses: 'all' });
    } else {
      updateSettings({ availableProjectStatuses: values as ProjectStatusType[] });
    }
    // Toast removed - multiselect provides visual feedback
  };

  const handleCostCentreStatusesChange = (values: string[]) => {
    if (values.includes('all')) {
      updateSettings({ availableCostCentreStatuses: 'all' });
    } else {
      updateSettings({ availableCostCentreStatuses: values as ProjectStatusType[] });
    }
    // Toast removed - multiselect provides visual feedback
  };

  const handleStatusTextChange = (id: string, text: string) => {
    // Define default status texts
    const defaultStatusTexts: Record<string, string> = {
      'planned': 'Planned',
      'confirmed': 'Confirmed',
      'cancelled': 'Cancelled',
      'completed': 'Completed',
    };
    
    const updatedConfigs = settings.statusConfigs.map(config => {
      if (String(config.id) === String(id)) {
        // If text is empty, revert to default
        const finalText = text.trim() === '' ? (defaultStatusTexts[id] || config.text) : text;
        return { ...config, text: finalText };
      }
      return config;
    });
    updateSettings({ statusConfigs: updatedConfigs });
  };

  const handleStatusColorChange = (id: string | number, color: string) => {
    const updatedConfigs = settings.statusConfigs.map(config =>
      String(config.id) === String(id) ? { ...config, color } : config
    );
    updateSettings({ statusConfigs: updatedConfigs });
    // Toast removed - color picker provides visual feedback
  };

  const handleStatusEnabledChange = (id: string | number, enabled: boolean) => {
    const updatedConfigs = settings.statusConfigs.map(config =>
      String(config.id) === String(id) ? { ...config, enabled } : config
    );
    updateSettings({ statusConfigs: updatedConfigs });
    // Toast removed - checkbox state provides visual feedback
  };

  const handleStatusCustomTextChange = (id: string | number, customText: string) => {
    const updatedConfigs = settings.statusConfigs.map(config =>
      String(config.id) === String(id) ? { ...config, customText } : config
    );
    updateSettings({ statusConfigs: updatedConfigs });
  };

  const handleStatusCustomTextBlur = (id: string | number) => {
    const status = settings.statusConfigs.find(config => String(config.id) === String(id));
    if (status) {
      const finalCustomText = status.customText?.trim() === '' ? undefined : status.customText;
      const updatedConfigs = settings.statusConfigs.map(config =>
        String(config.id) === String(id) ? { ...config, customText: finalCustomText } : config
      );
      updateSettings({ statusConfigs: updatedConfigs });
    }
  };

  const handleColorOverrideTextChange = (id: string, text: string) => {
    const updatedOverrides = settings.colorOverrides.map(override =>
      String(override.id) === String(id) ? { ...override, text } : override
    );
    updateSettings({ colorOverrides: updatedOverrides });
  };

  const handleColorOverrideColorChange = (id: string | number, color: string) => {
    const updatedOverrides = settings.colorOverrides.map(override =>
      String(override.id) === String(id) ? { ...override, color } : override
    );
    updateSettings({ colorOverrides: updatedOverrides });
    // Toast removed - color picker provides visual feedback
  };

  const handleColorOverrideEnabledChange = (id: string | number, enabled: boolean) => {
    const updatedOverrides = settings.colorOverrides.map(override =>
      String(override.id) === String(id) ? { ...override, enabled } : override
    );
    updateSettings({ colorOverrides: updatedOverrides });
  };

  const handleColorOverrideCustomTextChange = (id: string | number, customText: string) => {
    const updatedOverrides = settings.colorOverrides.map(override =>
      String(override.id) === String(id) ? { ...override, customText } : override
    );
    updateSettings({ colorOverrides: updatedOverrides });
  };

  const handleColorOverrideCustomTextBlur = (id: string | number) => {
    const override = settings.colorOverrides.find(o => String(o.id) === String(id));
    if (override) {
      const finalCustomText = override.customText?.trim() === '' ? undefined : override.customText;
      const updatedOverrides = settings.colorOverrides.map(o =>
        String(o.id) === String(id) ? { ...o, customText: finalCustomText } : o
      );
      updateSettings({ colorOverrides: updatedOverrides });
    }
  };

  const handleAddColorOverride = () => {
    const newOverride = {
      id: `override-${Date.now()}`,
      text: 'Override Text',
      color: '#d1d5db',
      enabled: true,
    };
    updateSettings({ colorOverrides: [...settings.colorOverrides, newOverride] });
    // Toast removed - new override appears in list
  };

  const handleDeleteColorOverride = (id: string | number) => {
    const updatedOverrides = settings.colorOverrides.filter(override => String(override.id) !== String(id));
    updateSettings({ colorOverrides: updatedOverrides });
    // Toast removed - override disappears from list
  };

  // Utilisation Tier handlers
  const handleUtilisationTierLabelChange = (id: string | number, customLabel: string) => {
    const updatedTiers = settings.utilisationTiers.map(tier =>
      String(tier.id) === String(id) ? { ...tier, customLabel } : tier
    );
    updateSettings({ utilisationTiers: updatedTiers });
  };

  const handleUtilisationTierLabelBlur = (id: string | number) => {
    const tier = settings.utilisationTiers.find(t => String(t.id) === String(id));
    if (tier) {
      const finalCustomLabel = tier.customLabel?.trim() === '' ? undefined : tier.customLabel;
      const updatedTiers = settings.utilisationTiers.map(t =>
        String(t.id) === String(id) ? { ...t, customLabel: finalCustomLabel } : t
      );
      updateSettings({ utilisationTiers: updatedTiers });
    }
  };

  const handleUtilisationTierColorChange = (id: string | number, color: string) => {
    const updatedTiers = settings.utilisationTiers.map(tier =>
      String(tier.id) === String(id) ? { ...tier, color } : tier
    );
    updateSettings({ utilisationTiers: updatedTiers });
  };

  const handleUtilisationBoundaryChange = (leftTierId: string | number, rightTierId: string | number, boundaryValue: number) => {
  const updatedTiers = utilisationTiers.map((t) =>
    String(t.id) === String(leftTierId)
      ? { ...t, maxPercentage: Math.max(t.minPercentage, boundaryValue - 1) }
      : String(t.id) === String(rightTierId)
        ? { ...t, minPercentage: Math.min(t.maxPercentage, boundaryValue) }
        : t
  );
  updateSettings({ utilisationTiers: updatedTiers });
};

const handleUtilisationTierToggle = (id: string | number, enabled: boolean) => {
  const toggled = utilisationTiers.map((t) => (String(t.id) === String(id) ? { ...t, enabled } : t));
  const enabledBands = toggled.filter((t) => t.enabled).sort((a, b) => a.minPercentage - b.minPercentage);

  if (enabledBands.length >= 1) {
    // Detect whether any band is intended to capture over-utilisation (>100%).
    // If so, the last enabled band keeps its maxPercentage (999) so it
    // captures everything above 100%. Otherwise we distribute 0–100 evenly.
    const hasOverBand = enabledBands.some((b) => b.maxPercentage > 100);
    const subHundredBands = hasOverBand ? enabledBands.slice(0, -1) : enabledBands;
    const overBand = hasOverBand ? enabledBands[enabledBands.length - 1] : null;

    const globalMin = 0;
    const globalMax = 100;
    const bandWidth = subHundredBands.length > 0
      ? (globalMax - globalMin) / subHundredBands.length
      : 0;

    const redistributed = toggled.map((t) => {
      if (!t.enabled) return t;
      // The over-utilisation band always starts at 101 and extends to 999
      if (overBand && String(t.id) === String(overBand.id)) {
        return { ...t, minPercentage: 101, maxPercentage: 999 };
      }
      const idx = subHundredBands.findIndex((b) => String(b.id) === String(t.id));
      if (idx === -1) return t;
      const newMin = idx === 0 ? globalMin : Math.ceil(globalMin + idx * bandWidth);
      const newMax =
        idx === subHundredBands.length - 1
          ? globalMax
          : Math.floor(globalMin + (idx + 1) * bandWidth - 0.01);
      return { ...t, minPercentage: newMin, maxPercentage: newMax };
    });
    updateSettings({ utilisationTiers: redistributed });
  } else {
    updateSettings({ utilisationTiers: toggled });
  }
};

const handleUtilisationTierRangeChange = (id: string, field: 'minPercentage' | 'maxPercentage', value: string) => {
    const numValue = parseInt(value);
    // Allow values from 0 up to 999 so over-utilisation bands (>100%) are supported
    if (isNaN(numValue) || numValue < 0 || numValue > 999) return;
    
    const updatedTiers = settings.utilisationTiers.map(tier =>
      String(tier.id) === String(id) ? { ...tier, [field]: numValue } : tier
    );
    updateSettings({ utilisationTiers: updatedTiers });
  };

  const handleResetUtilisationTiers = () => {
    updateSettings({ utilisationTiers: DEFAULT_UTILIZATION_TIERS });
    toast.success('Utilisation bands reset to defaults');
  };

  // Validation function for utilisation tiers
  // Removed: validateUtilisationTiers function (Demo only)

  const timezoneOptions = TIMEZONES.map(tz => ({
    value: tz.value,
    label: `${tz.label} (${tz.offset})`
  }));

  const handleBusinessHoursStartChange = (value: string) => {
    setLocalBusinessHoursStart(value);
  };

  const handleBusinessHoursEndChange = (value: string) => {
    setLocalBusinessHoursEnd(value);
  };
  
  const handleBusinessHoursStartBlur = () => {
    const hour = parseInt(localBusinessHoursStart.split(':')[0]);
    if (hour >= 0 && hour < 24) {
      updateSettings({ businessHoursStart: hour });
    }
  };

  const handleBusinessHoursEndBlur = () => {
    const hour = parseInt(localBusinessHoursEnd.split(':')[0]);
    if (hour >= 0 && hour < 24) {
      updateSettings({ businessHoursEnd: hour });
    }
  };

  const handleVisibleHoursStartChange = (value: string) => {
    setLocalVisibleHoursStart(value);
  };

  const handleVisibleHoursEndChange = (value: string) => {
    setLocalVisibleHoursEnd(value);
  };

  const handleVisibleHoursStartBlur = () => {
    const hour = parseInt(localVisibleHoursStart.split(':')[0]);
    if (!isNaN(hour) && hour >= 0 && hour <= 23) {
      updateSettings({ visibleHoursStart: hour });
    }
  };

  const handleVisibleHoursEndBlur = () => {
    const hour = parseInt(localVisibleHoursEnd.split(':')[0]);
    if (!isNaN(hour) && hour >= 0 && hour <= 23) {
      updateSettings({ visibleHoursEnd: hour });
    }
  };











  // Timeline Options handlers
  const handleTimelineTimeRangeChange = (value: string) => {
    // Update both timelineDefaultTimeRange and defaultCalendarView to keep them in sync
    const calendarView = value.toLowerCase() as CalendarView;
    updateSettings({ 
      timelineDefaultTimeRange: value,
      defaultCalendarView: calendarView 
    });
    // Toast removed - dropdown selection provides visual feedback
  };

  const handleTimelineBookingTypeChange = (value: string) => {
    updateSettings({ timelineDefaultBookingType: value });
    // Toast removed - dropdown selection provides visual feedback
  };

  const handleTimelineBookingStatusChange = (value: string) => {
    updateSettings({ timelineDefaultBookingStatus: value });
    // Toast removed - dropdown selection provides visual feedback
  };

  // Normalize legacy timeline view values to current format
  const normalizeTimelineView = (value: string | undefined): string => {
    if (!value) return 'resource';
    const normalized = value.toLowerCase();
    if (normalized === 'customer') return 'customer';
    if (normalized === 'project & resource' || normalized === 'project-resource') return 'project-resource';
    if (normalized === 'resource') return 'resource';
    if (normalized === 'role & resource' || normalized === 'role-resource') return 'role-resource';
    return 'resource';
  };

  const handleTimelineViewChange = (value: string) => {
    updateSettings({ timelineDefaultView: value });
    // Toast removed - dropdown selection provides visual feedback
  };

  const handleTimelineTimezoneChange = async (value: string) => {
    // Set user-level timezone override
    await setUserTimezone(value);
    
    toast.success('Personal timezone updated', {
      description: 'This timezone will be used for your timeline and new bookings.',
    });
  };

  const handleResetTimezoneToDefault = async () => {
    // Clear user override and revert to system default
    await clearUserTimezone();
    
    toast.success('Timezone reset to system default', {
      description: `Now using system default: ${systemDefault}`,
    });
  };

  // Initial loading: fetch existing calendar URL on mount
  useEffect(() => {
    const fetchCalendarUrl = async () => {
      if (!user?.id) return;
      setIsLoadingCalendarUrl(true);
      try {
        const response = await calendarSyncApi.generateLink({ userId: user.id });
        const url = response.icsFileUrl;
        if (url && url.trim() !== '') {
          setCalendarUrl(url);
        } else {
          setCalendarUrl('');
        }
      } catch {
        setCalendarUrl('');
      } finally {
        setIsLoadingCalendarUrl(false);
      }
    };
    fetchCalendarUrl();
  }, [user?.id]);

  // Calendar sync handlers (moved from Integrations page)
  const handleGenerateCalendarUrl = async () => {
    setIsGeneratingUrl(true);
    try {
      const response = await calendarSyncApi.generateLink({ userId: user.id });
      const url = response.icsFileUrl;
      if (url && url.trim() !== '') {
        setCalendarUrl(url);
      } else {
        setCalendarUrl('');
      }
      toast.success('Calendar URL generated');
    } catch (error) {
      toast.error('Failed to generate calendar URL', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsGeneratingUrl(false);
    }
  };

  const handleCopyCalendarUrl = () => {
    if (!calendarUrl) return;
    try {
      // Use a temporary textarea + execCommand as fallback for environments
      // where the Clipboard API is blocked by permissions policy (e.g. iframes)
      const textarea = document.createElement('textarea');
      textarea.value = calendarUrl;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('URL copied', { description: 'Calendar URL copied to clipboard.' });
    } catch {
      toast.error('Copy failed', { description: 'Could not copy to clipboard.' });
    }
  };

  const handleDeleteCalendarUrl = async () => {
    setIsDeletingUrl(true);
    try {
      await calendarSyncApi.deleteLink({ userId: user.id });
      setCalendarUrl('');
      setIsDeleteUrlDialogOpen(false);
      toast.success('Calendar URL deleted');
    } catch (error) {
      toast.error('Failed to delete calendar URL', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsDeletingUrl(false);
    }
  };



  // Helper function to format hour to HH:MM format
  const formatTimeValue = (hour: number): string => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  // Helper function to check if a section matches the search query
  // Helper function to check if a setting matches the search query
  const matchesSearch = (keywords: string[]): boolean => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return keywords.some(keyword => keyword.toLowerCase().includes(query));
  };

  // Individual setting visibility
  const showAbsenceSetting = matchesSearch(['absence', 'show absence', 'label override']);
  const showPublicHolidaysSetting = matchesSearch(['public holiday', 'holiday', 'show public holidays']);
  const showProjectStatusesSetting = matchesSearch(['project status', 'available project status']);
  const showCostCentreStatusesSetting = matchesSearch(['cost centre status', 'available cost centre status']);
  const showStatusSettingsSetting = matchesSearch(['status settings', 'status', 'color', 'colour', 'colour override', 'color override', 'override']);
  const showUtilisationSetting = matchesSearch(['utilisation', 'utilization', 'utilisation %', 'utilization %', 'tier', 'percentage', 'heatmap', 'bands', 'utilisation bands']);
  const showBookingTypeSetupSetting = matchesSearch(['booking type', 'booking type setup', 'type setup', 'auto update timesheet', 'repeat bookings']);
  const showBusinessHoursSetting = matchesSearch(['business hours', 'working hours', 'hours']);
  const showTimezonesSetting = matchesSearch(['timezone', 'time zone', 'multiple timezone']);
  const showDefaultResourceSetting = matchesSearch(['default resource', 'resource', 'label']);



  const showCalendarSyncSetting = matchesSearch(['calendar sync', 'external calendar', 'calendar url', 'ics', 'calendar feed']);
  const showConflictDetectionSetting = matchesSearch(['conflict detection', 'conflict', 'detection']);
  const showEmailNotificationsSetting = matchesSearch(['email notification', 'email', 'notification']);
  const showAutoSyncSetting = matchesSearch(['auto sync', 'sync', 'automatic']);

  const showTimelineDefaultsSetting = matchesSearch(['timeline', 'defaults', 'time range', 'booking type', 'booking status', 'timeline view']);
  const showUserPreferencesSetting = matchesSearch(['user preferences', 'preferences', 'confirm drag', 'drag drop', 'default resource', 'logged in resource', 'personal']);

  // Section visibility - show section if any setting within it matches
  const showAdminSection = isAdministrator && (
    showAbsenceSetting ||
    showPublicHolidaysSetting ||
    showProjectStatusesSetting ||
    showCostCentreStatusesSetting ||
    showStatusSettingsSetting ||
    showUtilisationSetting ||
    showBusinessHoursSetting ||
    showDefaultResourceSetting ||
    searchQuery.trim() !== '' || // Always show admin section when searching
    activeTab === 'admin' // Always show admin section when on admin tab
  );

  const showCalendarSyncSection = showCalendarSyncSetting;



  const showTimelineSection = showTimelineDefaultsSetting || showUserPreferencesSetting;

  const hasResults = showAdminSection || showTimelineSection || showCalendarSyncSection;

  // Auto-switch to timezone tab when searching for timezone-related terms
  useEffect(() => {
    if (showTimezonesSetting && activeTab === 'admin' && searchQuery.trim() !== '') {
      setAdminSubTab('timezone');
    }
  }, [showTimezonesSetting, activeTab, searchQuery]);

  // Auto-switch to booking types tab when searching for booking type-related terms
  useEffect(() => {
    if (showBookingTypeSetupSetting && activeTab === 'admin' && searchQuery.trim() !== '') {
      setAdminSubTab('booking-type-v2');
    }
  }, [showBookingTypeSetupSetting, activeTab, searchQuery]);

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
    <div className="w-full px-4 py-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {activeTab === 'admin' ? 'Administrator Settings' : activeTab === 'integrations' ? 'Integration Settings' : 'General Settings'}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <DataModeToggle screen="settings" />
        </div>
      </div>

      {/* Top-level tab navigation removed — now handled by sidebar menu */}

      {/* Integrations Tab */}
      {isAdministrator && activeTab === 'integrations' && (
        <Integrations />
      )}

      {/* Search Box */}
      {(!isAdministrator || activeTab === 'settings') && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search settings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      )}

      {/* Administrator Settings - Only visible to Administrators */}
      {showAdminSection && isAdministrator && activeTab === 'admin' && (
        <div className="space-y-6">
          {/* Admin Submenu */}
          <Tabs value={adminSubTab} onValueChange={setAdminSubTab} className="w-full">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="timezone">Timezone</TabsTrigger>
              {/* <TabsTrigger value="booking-types">Booking Type V1</TabsTrigger> */}
              <TabsTrigger value="booking-type-v2">Booking Type Setup</TabsTrigger>
              <TabsTrigger value="heatmap-settings">Heatmap Settings</TabsTrigger>
              <TabsTrigger value="planning">Planning</TabsTrigger>
              {settings.groupingMethod === 'scheduler-group' && (
                <TabsTrigger value="group-setup">Group Setup</TabsTrigger>
              )}
            </TabsList>
          </Tabs>

          <Card className="border-primary/20">
            <>
              {/* General Settings Tab */}
              {adminSubTab === 'general' && (
                <CardContent className="space-y-6 pt-6">
                  {/* Resource Rates Section - Collapsible */}
                  <Collapsible 
                    open={openGeneralSection === 'resourceRates'} 
                    onOpenChange={() => handleGeneralSectionToggle('resourceRates')}
                    className="space-y-4"
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex w-full items-center justify-between p-0 hover:bg-transparent"
                      >
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">Resource Rates</h3>
                        </div>
                        <ChevronDown className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
                      </Button>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent className="space-y-4">
                      {/* Show Resource Rates */}
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="show-rates">Show Resource Rates</Label>
                          <p className="text-xs text-muted-foreground">
                            Display resource rates in the application
                          </p>
                        </div>
                        <Switch
                          id="show-rates"
                          checked={settings.showResourceRates}
                          onCheckedChange={handleShowResourceRatesToggle}
                        />
                      </div>

                      {/* Copy Resource Rates - Only show if Show Resource Rates is enabled */}
                      {settings.showResourceRates && (
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label htmlFor="copy-rates">Copy Resource Rates</Label>
                            <p className="text-xs text-muted-foreground">
                              Include resource rates when copying or duplicating bookings
                            </p>
                          </div>
                          <Switch
                            id="copy-rates"
                            checked={settings.copyResourceRates}
                            onCheckedChange={handleCopyResourceRatesToggle}
                          />
                        </div>
                      )}
                    </CollapsibleContent>
                  </Collapsible>

                  <Separator />

                  {/* Match Score RAG Thresholds - Collapsible */}
                  <Collapsible
                    open={openGeneralSection === 'aiRag'}
                    onOpenChange={() => handleGeneralSectionToggle('aiRag')}
                    className="space-y-4"
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex w-full items-center justify-between p-0 hover:bg-transparent"
                      >
                        <div className="flex items-center gap-2">
                          <Zap className="h-4 w-4" />
                          <h3 className="font-semibold">Match Score Thresholds</h3>
                        </div>
                        <ChevronDown className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
                      </Button>
                    </CollapsibleTrigger>

                    <CollapsibleContent className="space-y-5">
                      <p className="text-sm text-muted-foreground">
                        Define the percentage thresholds that determine the RAG (Red / Amber / Green) colour rating shown on resource match scores across the app.
                      </p>

                      {/* Visual RAG preview */}
                      <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                          <span className="text-sm font-medium text-emerald-700">
                            Green ≥ {settings.aiRagThresholds?.greenThreshold ?? 80}%
                          </span>
                        </div>
                        <span className="text-muted-foreground">·</span>
                        <div className="flex items-center gap-1.5">
                          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                          <span className="text-sm font-medium text-amber-700">
                            Amber ≥ {settings.aiRagThresholds?.amberThreshold ?? 50}%
                          </span>
                        </div>
                        <span className="text-muted-foreground">·</span>
                        <div className="flex items-center gap-1.5">
                          <span className="h-2.5 w-2.5 rounded-full bg-destructive" />
                          <span className="text-sm font-medium text-destructive">
                            Red &lt; {settings.aiRagThresholds?.amberThreshold ?? 50}%
                          </span>
                        </div>
                      </div>

                      {/* Threshold inputs */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="rag-green" className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 inline-block" />
                            Green threshold (%)
                          </Label>
                          <Input
                            id="rag-green"
                            type="number"
                            min={1}
                            max={100}
                            value={settings.aiRagThresholds?.greenThreshold ?? 80}
                            onChange={e => handleRagThresholdChange('greenThreshold', e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">Scores at or above this value are shown in green</p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="rag-amber" className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full bg-amber-400 inline-block" />
                            Amber threshold (%)
                          </Label>
                          <Input
                            id="rag-amber"
                            type="number"
                            min={1}
                            max={100}
                            value={settings.aiRagThresholds?.amberThreshold ?? 50}
                            onChange={e => handleRagThresholdChange('amberThreshold', e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">Scores at or above this value (but below green) are shown in amber. Below this is red</p>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <Button variant="outline" size="sm" onClick={handleResetRagThresholds} className="gap-2">
                          <RotateCcw className="h-3.5 w-3.5" />
                          Reset to defaults (80 / 50)
                        </Button>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  <Separator />

                  {/* Match Signal Weighting - Collapsible */}
                  <Collapsible
                    open={openGeneralSection === 'aiWeights'}
                    onOpenChange={() => handleGeneralSectionToggle('aiWeights')}
                    className="space-y-4"
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex w-full items-center justify-between p-0 hover:bg-transparent"
                      >
                        <div className="flex items-center gap-2">
                          <BarChart3 className="h-4 w-4" />
                          <h3 className="font-semibold">Match Signal Weighting</h3>
                        </div>
                        <ChevronDown className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
                      </Button>
                    </CollapsibleTrigger>

                    <CollapsibleContent className="space-y-5">
                      <p className="text-sm text-muted-foreground">
                        Control how much each factor influences the overall match score. Higher weight = more impact on the final score. Weights should total 100%.
                      </p>

                      {/* Total indicator */}
                      <div className={cn(
                        'flex items-center justify-between rounded-lg border px-4 py-3 text-sm',
                        weightsValid
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-amber-200 bg-amber-50 text-amber-700'
                      )}>
                        <span className="font-medium">
                          {weightsValid ? '✓ Weights total 100%' : `⚠ Weights total ${weightsTotal}% — adjust sliders to reach 100%`}
                        </span>
                        <div className="flex gap-1">
                          {signalConfig.map(s => (
                            <div
                              key={s.key}
                              className="h-2 rounded-full bg-primary/70"
                              style={{ width: `${Math.max(2, currentWeights[s.key])}px` }}
                              title={`${s.label}: ${currentWeights[s.key]}%`}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Signal sliders */}
                      <div className="space-y-5">
                        {signalConfig.map(signal => (
                          <div key={signal.key} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div>
                                  <Label className="text-sm font-medium">{signal.label}</Label>
                                  <p className="text-xs text-muted-foreground">{signal.description}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={currentWeights[signal.key]}
                                  onChange={e => {
                                    const v = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                    handleSignalWeightChange(signal.key, v);
                                  }}
                                  className="h-8 w-16 text-center text-sm"
                                />
                                <span className="text-sm text-muted-foreground w-4">%</span>
                              </div>
                            </div>
                            <Slider
                              value={[currentWeights[signal.key]]}
                              onValueChange={([v]) => handleSignalWeightChange(signal.key, v)}
                              min={0}
                              max={100}
                              step={1}
                              className="w-full"
                            />
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-end">
                        <Button variant="outline" size="sm" onClick={handleResetSignalWeights} className="gap-2">
                          <RotateCcw className="h-3.5 w-3.5" />
                          Reset to defaults
                        </Button>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  <Separator />

                  {/* Defaults Section - Collapsible */}
                  <Collapsible 
                    open={openGeneralSection === 'defaults'} 
                    onOpenChange={() => handleGeneralSectionToggle('defaults')}
                    className="space-y-4"
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex w-full items-center justify-between p-0 hover:bg-transparent"
                      >
                        <div className="flex items-center gap-2">
                          <Settings2 className="h-4 w-4" />
                          <h3 className="font-semibold">Defaults</h3>
                        </div>
                        <ChevronDown className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
                      </Button>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent className="space-y-4">
                      {/* Business Hours Settings */}
                      {showBusinessHoursSetting && (
                        <>
                          <div className="space-y-4">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              <h4 className="font-medium">Business Hours</h4>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              {/* Start Time */}
                              <div className="space-y-2">
                                <Label htmlFor="business-hours-start">Start Time</Label>
                                <Input
                                  id="business-hours-start"
                                  type="time"
                                  value={localBusinessHoursStart}
                                  onChange={(e) => handleBusinessHoursStartChange(e.target.value)}
                                  onBlur={handleBusinessHoursStartBlur}
                                  className="w-full"
                                />
                                <p className="text-xs text-muted-foreground">
                                  When business hours begin (24-hour format)
                                </p>
                              </div>

                              {/* End Time */}
                              <div className="space-y-2">
                                <Label htmlFor="business-hours-end">End Time</Label>
                                <Input
                                  id="business-hours-end"
                                  type="time"
                                  value={localBusinessHoursEnd}
                                  onChange={(e) => handleBusinessHoursEndChange(e.target.value)}
                                  onBlur={handleBusinessHoursEndBlur}
                                  className="w-full"
                                />
                                <p className="text-xs text-muted-foreground">
                                  When business hours end (24-hour format)
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Visible Hours */}
                          <div className="space-y-4 pt-2">
                            <div className="flex items-start gap-2">
                              <Eye className="h-4 w-4 mt-0.5 text-muted-foreground" />
                              <div>
                                <h4 className="font-medium leading-none">Visible Hours</h4>
                                <p className="text-xs text-muted-foreground mt-1">
                                  The time range shown in Day and Week calendar views. Set a buffer either side of business hours so staff can see early starts and late finishes.
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label htmlFor="visible-hours-start">Visible From</Label>
                                <Input
                                  id="visible-hours-start"
                                  type="time"
                                  value={localVisibleHoursStart}
                                  onChange={(e) => handleVisibleHoursStartChange(e.target.value)}
                                  onBlur={handleVisibleHoursStartBlur}
                                  className="w-full"
                                />
                                <p className="text-xs text-muted-foreground">
                                  Earliest visible time slot (default 07:00)
                                </p>
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="visible-hours-end">Visible Until</Label>
                                <Input
                                  id="visible-hours-end"
                                  type="time"
                                  value={localVisibleHoursEnd}
                                  onChange={(e) => handleVisibleHoursEndChange(e.target.value)}
                                  onBlur={handleVisibleHoursEndBlur}
                                  className="w-full"
                                />
                                <p className="text-xs text-muted-foreground">
                                  Latest visible time slot (default 19:00)
                                </p>
                              </div>
                            </div>

                          </div>

                          <Separator />
                        </>
                      )}

                      {/* Quick Add Default */}
                      <div className="space-y-2">
                        <Label htmlFor="quick-add-default">Quick Add Default</Label>
                        <Select
                          value={settings.quickAddDefault ? String(settings.quickAddDefault) : undefined}
                          onValueChange={handleQuickAddDefaultChange}
                        >
                          <SelectTrigger id="quick-add-default">
                            <SelectValue placeholder="Select default booking type" />
                          </SelectTrigger>
                          <SelectContent>
                            {settings.bookingTypes.map((bookingType) => (
                              <SelectItem key={bookingType.id} value={String(bookingType.id)}>
                                {bookingType.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Default booking type for quick add functionality
                        </p>
                      </div>

                      <Separator />

                      {/* Description Default */}
                      <div className="space-y-2">
                        <Label htmlFor="description-default">Description Default</Label>
                        <Select
                          value={settings.descriptionDefault}
                          onValueChange={handleDescriptionDefaultChange}
                        >
                          <SelectTrigger id="description-default">
                            <SelectValue placeholder="Select description default" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="from-project">From Project</SelectItem>
                            <SelectItem value="from-project-description">From Project Description</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Default source for booking descriptions
                        </p>
                      </div>

                      <Separator />

                      {/* Grouping Method */}
                      <div className="space-y-2">
                        <Label htmlFor="grouping-method">Grouping Method</Label>
                        <Select
                          value={settings.groupingMethod}
                          onValueChange={handleGroupingMethodChange}
                        >
                          <SelectTrigger id="grouping-method">
                            <SelectValue placeholder="Select grouping method" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="scheduler-group">Scheduler Group</SelectItem>
                            <SelectItem value="department">Department</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Choose how resources are grouped in the calendar view
                        </p>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  <Separator />

                  {/* Available Costing Statuses Section - Collapsible */}
                  <Collapsible 
                    open={openGeneralSection === 'costingStatuses'} 
                    onOpenChange={() => handleGeneralSectionToggle('costingStatuses')}
                    className="space-y-4"
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex w-full items-center justify-between p-0 hover:bg-transparent"
                      >
                        <div className="flex items-center gap-2">
                          <BarChart3 className="h-4 w-4" />
                          <h3 className="font-semibold">Available Costing Statuses</h3>
                        </div>
                        <ChevronDown className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
                      </Button>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent className="space-y-4">
                      {/* Project Status Settings */}
                      {showProjectStatusesSetting && (
                        <>
                          <div className="space-y-4">
                            <div className="flex items-center gap-2">
                              <Shield className="h-4 w-4" />
                              <h4 className="font-medium">Available Project Statuses</h4>
                            </div>

                            <div>
                              <MultiSelect
                                options={[{ value: 'all', label: 'All' }, ...PROJECT_STATUSES]}
                                value={settings.availableProjectStatuses === 'all' ? ['all'] : settings.availableProjectStatuses}
                                onValueChange={handleProjectStatusesChange}
                                placeholder="Select project statuses"
                                maxDisplay={2}
                              />
                              <p className="text-xs text-muted-foreground mt-2">
                                Select which project statuses should be available in the system
                              </p>
                            </div>
                          </div>

                          <Separator />
                        </>
                      )}

                      {/* Cost Centre Status Settings */}
                      {showCostCentreStatusesSetting && (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4" />
                            <h4 className="font-medium">Available Cost Centre Statuses</h4>
                          </div>

                          <div>
                            <MultiSelect
                              options={[{ value: 'all', label: 'All' }, ...PROJECT_STATUSES]}
                              value={settings.availableCostCentreStatuses === 'all' ? ['all'] : settings.availableCostCentreStatuses}
                              onValueChange={handleCostCentreStatusesChange}
                              placeholder="Select cost centre statuses"
                              maxDisplay={2}
                            />
                            <p className="text-xs text-muted-foreground mt-2">
                              Select which cost centre statuses should be available in the system
                            </p>
                          </div>
                        </div>
                      )}
                    </CollapsibleContent>
                  </Collapsible>

                  <Separator />

            {/* Status Settings */}
            {showStatusSettingsSetting && (
              <>
                <Collapsible 
                  open={openStatusSection === 'statusSettings'} 
                  onOpenChange={() => handleStatusSectionToggle('statusSettings')}
                  className="space-y-4"
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex w-full items-center justify-between p-0 hover:bg-transparent"
                    >
                      <div className="flex items-center gap-2">
                        <Palette className="h-4 w-4" />
                        <h3 className="font-semibold">Status Settings</h3>
                      </div>
                      <ChevronDown className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
                    </Button>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent className="space-y-4">
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Configure the text and color for each booking status
                      </p>
                      <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-md">
                        <div className="flex-shrink-0 mt-0.5">
                          <svg className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="text-xs text-blue-900 dark:text-blue-100">
                          <strong>Retroactive Updates:</strong> When you change a status color, all existing bookings with that status will automatically update to display the new color across all views in real-time.
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center font-medium text-sm text-muted-foreground pb-2">
                        <div></div>
                        <div>Status Text</div>
                        <div>Colour</div>
                      </div>

                      {settings.statusConfigs.map((status) => {
                        // Per-status descriptions — shown as subtext so admins
                        // understand what each status does before renaming it.
                        // These descriptions are fixed guidance text; only the
                        // display name and colour are customisable.
                        const STATUS_DESCRIPTIONS: Record<string, { description: string; heatmapNote?: string }> = {
                          planned: {
                            description: 'Work that is tentatively scheduled but not yet confirmed. Bookings can still be moved or cancelled.',
                            heatmapNote: 'Heatmap cells show a dashed border + ◈ prefix when all bookings are Planned (or other non-confirmed statuses).',
                          },
                          confirmed: {
                            description: 'Work that has been reviewed and locked in. Confirmed bookings count as firm capacity in heatmap utilisation calculations.',
                            heatmapNote: 'Confirmed bookings always count towards solid (non-dashed) cell rendering on the heatmap.',
                          },
                          cancelled: {
                            description: 'Work that was planned or confirmed but has since been called off. Cancelled bookings are excluded from utilisation calculations.',
                            heatmapNote: 'Cancelled bookings are treated as non-confirmed on the heatmap — a cell with only Cancelled bookings shows the dashed border treatment.',
                          },
                          completed: {
                            description: 'Work that has been carried out. Completed bookings count as firm capacity, the same as Confirmed.',
                            heatmapNote: 'Completed bookings always count towards solid (non-dashed) cell rendering on the heatmap.',
                          },
                        };
                        const meta = STATUS_DESCRIPTIONS[String(status.id)];

                        return (
                          <div key={status.id} className="space-y-1.5">
                            <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center">
                              <Checkbox
                                id={`status-${status.id}`}
                                checked={status.enabled}
                                onCheckedChange={(checked) =>
                                  handleStatusEnabledChange(status.id, checked as boolean)
                                }
                              />
                              <Input
                                value={status.customText ?? ''}
                                onChange={(e) => handleStatusCustomTextChange(status.id, e.target.value)}
                                onBlur={() => handleStatusCustomTextBlur(status.id)}
                                onFocus={(e) => e.target.setSelectionRange(0, 0)}
                                placeholder={status.text}
                                className="h-8 text-sm"
                                disabled={!status.enabled}
                              />
                              <ColorPicker
                                value={status.color}
                                onChange={(color) => handleStatusColorChange(status.id, color)}
                                disabled={!status.enabled}
                              />
                            </div>
                            {meta && (
                              <div className="pl-7 space-y-0.5">
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  {meta.description}
                                </p>
                                {meta.heatmapNote && (
                                  <p className="text-xs text-muted-foreground/70 leading-relaxed italic">
                                    {meta.heatmapNote}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
											  
																													  
													
						  
                    </div>

                    <Separator className="my-6" />

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sm">Colour Overrides</h4>
                        <p className="text-sm text-muted-foreground">
                          Active colour overrides (marked with a tick) appear as selectable options in the booking form's colour dropdown when the colour field is enabled. Selecting an override replaces the default status colour when viewing bookings on the timeline.
                        </p>
                      </div>

                      <div className="space-y-3">
                        <div className="grid grid-cols-[auto_1fr_auto_auto] gap-3 items-center font-medium text-sm text-muted-foreground pb-2">
                          <div></div>
                          <div>Override Text</div>
                          <div>Colour</div>
                          <div></div>
                        </div>

                        {settings.colorOverrides.map((override) => (
                          <div key={override.id} className="grid grid-cols-[auto_1fr_auto_auto] gap-3 items-center">
                            <Checkbox
                              id={`override-${override.id}`}
                              checked={override.enabled}
                              onCheckedChange={(checked) => 
                                handleColorOverrideEnabledChange(override.id, checked as boolean)
                              }
                            />
                            <Input
                              value={override.customText || ''}
                              onChange={(e) => handleColorOverrideCustomTextChange(override.id, e.target.value)}
                              onBlur={() => handleColorOverrideCustomTextBlur(override.id)}
                              placeholder={override.text}
                              className="h-8 text-sm"
                              disabled={!override.enabled}
                            />
                            <ColorPicker
                              value={override.color}
                              onChange={(color) => handleColorOverrideColorChange(override.id, color)}
                              disabled={!override.enabled}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteColorOverride(override.id)}
                              className="h-9 w-9"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}

                        <Button
                          onClick={handleAddColorOverride}
                          variant="outline"
                          className="w-full mt-2"
                        >
                          Add Override
                        </Button>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
					  

                <Separator />
              </>
            )}



            {/* Absence & Holiday Settings */}
            <Collapsible 
              open={openAbsenceSection === 'absenceSettings'} 
              onOpenChange={() => handleAbsenceSectionToggle('absenceSettings')}
              className="space-y-4"
            >
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex w-full items-center justify-between p-0 hover:bg-transparent"
                >
                  <div className="flex items-center gap-2">
                    <CalendarClock className="h-4 w-4" />
                    <h3 className="font-semibold">Absence & Holiday Settings</h3>
                  </div>
                  <ChevronDown className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
                </Button>
              </CollapsibleTrigger>

              <CollapsibleContent className="space-y-4">
                <div className="space-y-4">
                  {/* Absence Color */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5 flex-1">
                      <Label htmlFor="absence-color">Absence Color</Label>
                      <p className="text-sm text-muted-foreground">
                        Choose the display color for absence in the calendar
                      </p>
                    </div>
                    <ColorPicker
                      value={settings.absenceColor}
                      onChange={handleAbsenceColorChange}
                    />
                  </div>

                  {/* Absence Label Override */}
                  <div className="space-y-2">
                    <Label htmlFor="absence-label">Absence Label Override</Label>
                    <Input
                      id="absence-label"
                      placeholder="Enter custom label (optional)"
                      value={settings.absenceLabelOverride || ''}
                      onChange={(e) => handleAbsenceLabelChange(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave empty to use the default "Absence" label
                    </p>
                  </div>

                  {/* Public Holiday Color */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5 flex-1">
                      <Label htmlFor="holiday-color">Public Holiday Color</Label>
                      <p className="text-sm text-muted-foreground">
                        Choose the display color for public holidays in the calendar
                      </p>
                    </div>
                    <ColorPicker
                      value={settings.publicHolidayColor}
                      onChange={handlePublicHolidayColorChange}
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
              </CardContent>
            )}

            {/* Timezone Settings Tab */}
            {adminSubTab === 'timezone' && (
              <CardContent className="space-y-6 pt-6">
                {/* Timezone Settings Collapsible Section */}
                <Collapsible
                  open={openTimezoneSection === 'timezoneSettings'}
                  onOpenChange={() => handleTimezoneSectionToggle('timezoneSettings')}
                  className="space-y-2"
                >
                  <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-transparent rounded-lg transition-colors">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      <h3 className="font-semibold">Timezone Settings</h3>
                    </div>
                    {openTimezoneSection === 'timezoneSettings' ? (
                      <ChevronUp className="h-4 w-4 transition-transform" />
                    ) : (
                      <ChevronDown className="h-4 w-4 transition-transform" />
                    )}
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent className="space-y-4 pt-4">
                    <div className="px-4 space-y-4">
                      {/* Feature Toggle */}
                      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                        <div className="space-y-0.5 flex-1">
                          <Label htmlFor="enable-timezones" className="text-base font-semibold">
                            Enable Multiple Timezones
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Configure timezone settings for your organization. Multiple timezones allow users to view and work with schedules across different time zones.
                          </p>
                        </div>
                        <Switch
                          id="enable-timezones"
                          checked={settings.enableTimezones}
                          onCheckedChange={handleTimezonesToggle}
                        />
                      </div>

                      {!settings.enableTimezones && (
                        <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <p className="text-sm text-blue-900 dark:text-blue-100">
                            <strong>Default Timezone:</strong> Europe/London (UK)
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                            When multiple timezones are disabled, the system operates using UK timezone exclusively.
                          </p>
                        </div>
                      )}
                    </div>

                    <Separator />

                    <div className={`px-4 space-y-6 ${!settings.enableTimezones ? 'opacity-50 pointer-events-none' : ''}`}>
                      {/* Available Timezones */}
                      <div className="space-y-2">
                        <Label htmlFor="available-timezones">Available Timezones</Label>
                        <MultiSelect
                          options={timezoneOptions}
                          placeholder="Select timezones..."
                          emptyMessage="No timezones found."
                          maxDisplay={2}
                          value={settings.availableTimezones}
                          onValueChange={handleTimezonesChange}
                        />
                        <p className="text-xs text-muted-foreground">
                          Select which timezones will be available to users. Selected timezones will appear in timezone selectors throughout the application.
                        </p>
                      </div>

                      <Separator />

                      {/* System Default Timezone */}
                      <div className="space-y-2">
                        <Label htmlFor="default-timezone">System Default Timezone</Label>
                        <Select
                          value={settings.defaultTimezone}
                          onValueChange={handleDefaultTimezoneChange}
                        >
                          <SelectTrigger id="default-timezone">
                            <SelectValue placeholder="Select default timezone" />
                          </SelectTrigger>
                          <SelectContent>
                            {TIMEZONES
                              .filter((tz) => settings.availableTimezones.includes(tz.value))
                              .map((tz) => (
                                <SelectItem key={tz.value} value={tz.value}>
                                  {tz.label} ({tz.offset})
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">
                            The default timezone used throughout the system for all users. Only timezones selected in Available Timezones will appear here.
                          </p>
                          {currentMode === 'demo' && (
                            <p className="text-xs text-amber-600 font-medium">
                              ⚠︝ Demo Mode: Changes won't persist to database. Switch to Live mode to save permanently.
                            </p>
                          )}
                          {import.meta.env.DEV && (
                            <p className="text-xs text-muted-foreground font-mono">
                              Debug: mode={currentMode} | saved={settings.defaultTimezone} | available={settings.availableTimezones.length} timezones
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            )}

            {/* Booking Type V1 Tab - Hidden */}
            {/* {adminSubTab === 'booking-types' && (
              <CardContent className="pt-6">
                <BookingTypeSetup
                  bookingTypes={migratedBookingTypes as any}
                  bookings={bookings}
                  onSave={(bookingType) => {
                    const existing = migratedBookingTypes.find(bt => String(bt.id) === String(bookingType.id));
                    let updatedBookingTypes;
                    if (existing) {
                      updatedBookingTypes = migratedBookingTypes.map(bt =>
                        String(bt.id) === String(bookingType.id) ? bookingType : bt
                      ) as any;
                    } else {
                      updatedBookingTypes = [...migratedBookingTypes, bookingType] as any;
                    }
                    updateSettings({ bookingTypes: updatedBookingTypes });
                    if (!existing) {
                      setSelectedBookingTypeId(String(bookingType.id));
                    }
                  }}
                  onDelete={(id) => {
                    if (migratedBookingTypes.length <= 1) {
                      toast.error('Cannot delete the last booking type');
                      return;
                    }
                    const updatedBookingTypes = migratedBookingTypes.filter(bt => String(bt.id) !== String(id)) as any;
                    updateSettings({ bookingTypes: updatedBookingTypes });
                    if (String(selectedBookingTypeId) === String(id)) {
                      setSelectedBookingTypeId(String(updatedBookingTypes[0]?.id || ''));
                    }
                  }}
                />
              </CardContent>
            )} */}

            {/* Booking Type Setup Tab */}
            {adminSubTab === 'booking-type-v2' && (
              <CardContent className="pt-6 relative">
                {isContextLoading && (
                  <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] z-50 flex items-center justify-center rounded-lg">
                    <div className="flex items-center gap-3 bg-background border rounded-lg px-5 py-3 shadow-lg">
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                      <span className="text-sm font-medium">Loading Booking Types...</span>
                    </div>
                  </div>
                )}
                <BookingTypeV2
                  bookingTypes={migratedBookingTypes as any}
                  bookings={bookings}
                  resources={resources.filter(r => String(r.id) !== 'unallocated')}
                  onSave={async (bookingType) => {
                    // Pass ONLY the single changed/new booking type in the array.
                    // SchedulerContext will merge it into the existing array for
                    // local state and only make API calls for this one type,
                    // avoiding unnecessary API calls for all other booking types.
                    await updateSettings({ bookingTypes: [bookingType] as any });
                  }}
                  onDelete={(id) => {
                    if (migratedBookingTypes.length <= 1) {
                      toast.error('Cannot delete the last booking type');
                      return;
                    }
                    const updatedBookingTypes = migratedBookingTypes.filter(bt => String(bt.id) !== String(id)) as any;
                    updateSettings({ bookingTypes: updatedBookingTypes });
                  }}
                  dataMode={currentMode}
                  demoBookingTypeUsages={demoBookingTypeUsages}
                  onDemoUsageChange={(bookingTypeId, resourceId, level) => {
                    setDemoBookingTypeUsages(prev => {
                      const filtered = prev.filter(u => !(String(u.bookingTypeId) === bookingTypeId && String(u.resourceId) === resourceId));
                      if (level === 'none') return filtered;
                      return [...filtered, {
                        id: Date.now(),
                        resourceId: Number(resourceId) || 0,
                        bookingTypeId: bookingTypeId as unknown as number,
                        isReadOnly: level === 'read-only',
                        isFullAccess: level === 'full-access',
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                      }];
                    });
                  }}
                  onDemoBulkUsageChange={(bookingTypeId, resourceIds, level) => {
                    setDemoBookingTypeUsages(prev => {
                      const filtered = prev.filter(u => !(String(u.bookingTypeId) === bookingTypeId && resourceIds.includes(String(u.resourceId))));
                      if (level === 'none') return filtered;
                      return [...filtered, ...resourceIds.map((rid, idx) => ({
                        id: Date.now() + idx,
                        resourceId: Number(rid) || 0,
                        bookingTypeId: bookingTypeId as unknown as number,
                        isReadOnly: level === 'read-only',
                        isFullAccess: level === 'full-access',
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                      }))];
                    });
                  }}
                />
              </CardContent>
            )}

            {/* ── Heatmap Settings Tab ────────────────────────────────── */}
            {adminSubTab === 'heatmap-settings' && (
              <CardContent className="space-y-6 pt-6">

                {/* ── Section 1: Utilisation Bands ──────────────────────── */}
                <div className="space-y-1">
                  <h3 className="font-semibold flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-primary" />
                    Utilisation Bands
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Define the percentage ranges that map to each utilisation band. These bands drive the colour coding across all heatmap views, legends, and hover tooltips. Drag the slider handles to shift boundaries; use the toggles to include or exclude individual bands.
                  </p>
                </div>

                <Separator />

                {/* Interactive Slider */}
                <div className="space-y-2">
                  <Label>Threshold boundaries</Label>
                  <p className="text-xs text-muted-foreground">Drag the handles to move the boundary between adjacent bands.</p>
                  <UtilisationSlider tiers={utilisationTiers} onBoundaryChange={handleUtilisationBoundaryChange} />
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center font-medium text-sm text-muted-foreground pb-2">
                    <div>On</div>
                    <div>Band label</div>
                    <div>Colour</div>
                  </div>

                  {(() => {
                    const descriptors: Record<string, string> = {
                      'tier-1': 'Resource has unallocated capacity.',
                      'tier-2': 'Within target utilisation range.',
                      'tier-3': 'Approaching full allocation — review before adding bookings.',
                      'tier-4': 'Bookings exceed available capacity.',
                    };
                    return utilisationTiers.filter((tier) => tier.label !== 'No bookings').map((tier) => (
                      <div key={tier.id} className="space-y-1">
                        <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center">
                          <Switch
                            checked={tier.enabled}
                            onCheckedChange={(checked) => handleUtilisationTierToggle(tier.id, checked)}
                          />
                          <Input
                            value={tier.customLabel || ''}
                            onChange={(e) => handleUtilisationTierLabelChange(tier.id, e.target.value)}
                            onBlur={() => handleUtilisationTierLabelBlur(tier.id)}
                            placeholder={tier.label}
                            className="h-8 text-sm"
                            disabled={!tier.enabled}
                          />
                          <ColorPicker
                            value={tier.color}
                            onChange={(color) => handleUtilisationTierColorChange(tier.id, color)}
                            disabled={!tier.enabled}
                          />
                        </div>
                        {descriptors[tier.id] && (
                          <p className="text-xs text-muted-foreground pl-[calc(theme(spacing.9)+theme(spacing.3))]">
                            {descriptors[tier.id]}
                          </p>
                        )}
                      </div>
                    ));
                  })()}
                </div>

                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResetUtilisationTiers}
                    className="shrink-0 text-xs"
                  >
                    Reset to defaults
                  </Button>
                </div>

                <Separator />

                {/* ── Section 2: Forecast View Settings ─────────────────── */}
                <div className="space-y-1">
                  <h3 className="font-semibold flex items-center gap-2">
                    <CalendarClock className="h-4 w-4 text-primary" />
                    Forecast View Settings
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Control how planned (unconfirmed) bookings are signalled on the Month, Quarter, and Year heatmap views. Each view has its own threshold because forecast confidence naturally decreases the further out you plan.
                  </p>
                </div>

                <Separator />

                {/* Per-view thresholds */}
                <div className="space-y-6">
                  <div className="space-y-1">
                    <h4 className="text-sm font-semibold">Planned-Only Warning Thresholds</h4>
                    <p className="text-xs text-muted-foreground">
                      When a heatmap cell contains <strong>only planned (unconfirmed) bookings</strong>, a corner triangle indicator appears once utilisation reaches the threshold for that view. Below the threshold, only the dashed border and reduced opacity are applied.
                    </p>
                  </div>

                  {(() => {
                    const DEFAULT_FS = { monthThreshold: 70 as number, quarterThreshold: 60 as number, yearThreshold: 50 as number, showPlannedPrefix: true as boolean, showConfirmedPlannedSplit: true as boolean };
                    const fs = { ...DEFAULT_FS, ...settings.forecastSettings };
                    const patchFs = (patch: Partial<typeof DEFAULT_FS>) => updateSettings({ forecastSettings: { ...fs, ...patch } });
                    return (
                      <>
                        {/* Month */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label className="text-sm font-medium">Month view threshold</Label>
                              <p className="text-xs text-muted-foreground">Near-term — bookings are relatively confirmed. Default 70%.</p>
                            </div>
                            <span className="text-sm font-semibold tabular-nums text-primary min-w-[3ch] text-right">{fs.monthThreshold}%</span>
                          </div>
                          <input type="range" min={40} max={100} step={5} value={fs.monthThreshold}
                            onChange={(e) => patchFs({ monthThreshold: parseInt(e.target.value) })}
                            className="w-full accent-primary" />
                          <div className="flex justify-between text-xs text-muted-foreground"><span>60% — flag earlier</span><span>100% — only when overloaded</span></div>
                        </div>

                        {/* Quarter */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label className="text-sm font-medium">Quarter view threshold</Label>
                              <p className="text-xs text-muted-foreground">Medium-term — moderate uncertainty. Default 60%.</p>
                            </div>
                            <span className="text-sm font-semibold tabular-nums text-primary min-w-[3ch] text-right">{fs.quarterThreshold}%</span>
                          </div>
                          <input type="range" min={40} max={100} step={5} value={fs.quarterThreshold}
                            onChange={(e) => patchFs({ quarterThreshold: parseInt(e.target.value) })}
                            className="w-full accent-primary" />
                          <div className="flex justify-between text-xs text-muted-foreground"><span>60% — flag earlier</span><span>100% — only when overloaded</span></div>
                        </div>

                        {/* Year */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label className="text-sm font-medium">Year view threshold</Label>
                              <p className="text-xs text-muted-foreground">Long-horizon — highly speculative. Flag demand earlier. Default 50%.</p>
                            </div>
                            <span className="text-sm font-semibold tabular-nums text-primary min-w-[3ch] text-right">{fs.yearThreshold}%</span>
                          </div>
                          <input type="range" min={40} max={100} step={5} value={fs.yearThreshold}
                            onChange={(e) => patchFs({ yearThreshold: parseInt(e.target.value) })}
                            className="w-full accent-primary" />
                          <div className="flex justify-between text-xs text-muted-foreground"><span>60% — flag earlier</span><span>100% — only when overloaded</span></div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <Separator />

                {/* ── Section 3: Display Options ─────────────────────────── */}
                {(() => {
                  const DEFAULT_FS = { monthThreshold: 70 as number, quarterThreshold: 60 as number, yearThreshold: 50 as number, showPlannedPrefix: true as boolean, showConfirmedPlannedSplit: true as boolean };
                  const fs = { ...DEFAULT_FS, ...settings.forecastSettings };
                  const patchFs = (patch: Partial<typeof DEFAULT_FS>) => updateSettings({ forecastSettings: { ...fs, ...patch } });
                  return (
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <h3 className="font-semibold flex items-center gap-2">
                          <Eye className="h-4 w-4 text-primary" />
                          Display Options
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          These options apply to planned (unconfirmed) bookings across all heatmap views.
                        </p>
                      </div>

                      <Separator />

                      <div className="flex items-center justify-between gap-4">
                        <div className="space-y-0.5">
                          <Label htmlFor="show-planned-prefix">◈ prefix on cell labels</Label>
                          <p className="text-xs text-muted-foreground">Prepends a small diamond symbol to planned-only cell labels (e.g. ◈ Planned) so the label itself signals unconfirmed data without needing to hover.</p>
                        </div>
                        <Switch id="show-planned-prefix" checked={fs.showPlannedPrefix}
                          onCheckedChange={(checked) => patchFs({ showPlannedPrefix: checked })} />
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <div className="space-y-0.5">
                          <Label htmlFor="show-split">Confirmed / planned split in tooltip</Label>
                          <p className="text-xs text-muted-foreground">Shows the confirmed vs. planned hour breakdown in the hover tooltip. Turn off if your team books exclusively as planned and the split adds noise.</p>
                        </div>
                        <Switch id="show-split" checked={fs.showConfirmedPlannedSplit}
                          onCheckedChange={(checked) => patchFs({ showConfirmedPlannedSplit: checked })} />
                      </div>
                    </div>
                  );
                })()}

                <Separator />

                {/* Reset all */}
                <div className="flex items-center justify-end">
                  <Button variant="outline" size="sm" className="shrink-0 text-xs"
                    onClick={() => updateSettings({ forecastSettings: { monthThreshold: 70, quarterThreshold: 60, yearThreshold: 50, showPlannedPrefix: true, showConfirmedPlannedSplit: true } })}>
                    Reset to defaults
                  </Button>
                </div>
              </CardContent>
            )}

            {/* Planning Tab */}
            {adminSubTab === 'planning' && (
              <CardContent className="space-y-6 pt-6">

                {/* ── Master toggle ──────────────────────────────────────── */}
                <div className="space-y-1">
                  <h3 className="font-semibold text-base">Capacity risk &amp; conflict resolution</h3>
                  <p className="text-sm text-muted-foreground">
                    When enabled, the scheduler monitors resource utilisation, surfaces at-risk
                    bookings in the heatmap view, and provides a What-if Analysis overlay to help
                    resolve conflicts before they impact delivery.
                  </p>
                </div>

                <div className={`rounded-lg border p-4 flex items-center justify-between gap-4 transition-colors ${whatIfEnabled ? 'bg-card border-border' : 'bg-muted/30 border-border'}`}>
                  <div className="space-y-0.5">
                    <Label htmlFor="what-if-master" className="text-sm font-semibold cursor-pointer">
                      Enable capacity risk monitoring
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {whatIfEnabled
                        ? 'Burnout risk alerts and the What-if Analysis overlay are active.'
                        : 'All capacity risk features are hidden. Planners see the scheduler only.'}
                    </p>
                  </div>
                  <Switch
                    id="what-if-master"
                    checked={whatIfEnabled}
                    onCheckedChange={toggleWhatIf}
                  />
                </div>

                {/* ── Resolution strategies (only shown when master is on) ── */}
                {whatIfEnabled && (
                  <>
                    <Separator />

                    <div className="space-y-1">
                      <h3 className="font-semibold text-sm">Resolution strategies</h3>
                      <p className="text-xs text-muted-foreground">
                        Choose which options appear in the What-if Analysis overlay when a conflict
                        is detected. At least one must remain enabled.
                      </p>
                    </div>

                    <div className="space-y-3">
                      {RESOLUTION_OPTION_CONFIGS.map((opt) => {
                        const on = isResolutionEnabled(opt.id);
                        return (
                          <div
                            key={opt.id}
                            className={`rounded-lg border p-4 transition-colors ${
                              on ? 'border-border bg-card' : 'border-border bg-muted/30'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="space-y-1 flex-1">
                                <Label
                                  htmlFor={`resolution-${opt.id}`}
                                  className={`text-sm font-semibold cursor-pointer ${
                                    on ? 'text-foreground' : 'text-muted-foreground'
                                  }`}
                                >
                                  {opt.label}
                                </Label>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  {opt.guidance}
                                </p>
                              </div>
                              <Switch
                                id={`resolution-${opt.id}`}
                                checked={on}
                                onCheckedChange={() => toggleResolutionOption(opt.id)}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </CardContent>
            )}

            {/* Group Setup Tab */}
            {adminSubTab === 'group-setup' && settings.groupingMethod === 'scheduler-group' && (
              <GroupSetupTab 
                currentMode={currentMode}
                schedulerGroups={schedulerGroups}
                resources={resources}
                addSchedulerGroup={addSchedulerGroup}
                updateSchedulerGroup={updateSchedulerGroup}
                saveSchedulerGroup={saveSchedulerGroup}
                deleteSchedulerGroup={deleteSchedulerGroup}
              />
            )}
            </>
          </Card>

        </div>
      )}

      {/* Timeline Options */}
      {showTimelineSection && (!isAdministrator || activeTab === 'settings') && (
        <Card>
          <CardContent className="space-y-6 pt-6">
            {showTimelineDefaultsSetting && (
              <Collapsible 
                open={openSettingsSection === 'defaults'} 
                onOpenChange={() => handleSettingsSectionToggle('defaults')}
              >
                <CollapsibleTrigger className="flex items-center gap-2 mb-4 w-full hover:opacity-70 transition-opacity">
                  <Clock className="h-4 w-4" />
                  <h3 className="font-semibold">Defaults</h3>
                  {openSettingsSection === 'defaults' ? (
                    <ChevronUp className="h-4 w-4 ml-auto" />
                  ) : (
                    <ChevronDown className="h-4 w-4 ml-auto" />
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-4">
                  {/* Time Range */}
                  <div className="space-y-2">
                    <Label htmlFor="timeline-time-range">Time Range</Label>
                    <Select
                      value={settings.timelineDefaultTimeRange}
                      onValueChange={handleTimelineTimeRangeChange}
                    >
                      <SelectTrigger id="timeline-time-range">
                        <SelectValue placeholder="Select time range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Day">Day</SelectItem>
                        <SelectItem value="Week">Week</SelectItem>
                        <SelectItem value="Month">Month</SelectItem>
                        <SelectItem value="Quarter">Quarter</SelectItem>
                        <SelectItem value="Year">Year</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Default time period displayed when opening the timeline view
                    </p>
                  </div>

                                    {/* Booking Type */}
                                    <div className="space-y-2">
                                      <Label htmlFor="timeline-booking-type">Booking Type</Label>
                                      <Select
                                        value={settings.timelineDefaultBookingType ? String(settings.timelineDefaultBookingType) : undefined}
                                        onValueChange={handleTimelineBookingTypeChange}
                                      >
                                        <SelectTrigger id="timeline-booking-type">
                                          <SelectValue placeholder="Select booking type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {settings.bookingTypes.filter(bt => bt.active).map((bookingType) => (
                                            <SelectItem key={bookingType.id} value={String(bookingType.id)}>
                                              {bookingType.name}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      <p className="text-xs text-muted-foreground">
                                        Default booking type selected when creating new bookings
                                      </p>
                                    </div>

                  {/* Booking Status */}
                  <div className="space-y-2">
                    <Label htmlFor="timeline-booking-status">Booking Status</Label>
                    <Select
                      value={settings.timelineDefaultBookingStatus}
                      onValueChange={handleTimelineBookingStatusChange}
                    >
                      <SelectTrigger id="timeline-booking-status">
                        <SelectValue placeholder={settings.statusConfigs.filter(s => s.enabled).length === 0 ? "No statuses configured" : "Select booking status"} />
                      </SelectTrigger>
                      <SelectContent>
                        {settings.statusConfigs.filter(s => s.enabled).length === 0 ? (
                          <SelectItem value="none" disabled>No statuses available</SelectItem>
                        ) : (
                          settings.statusConfigs
                            .filter(s => s.enabled)
                            .map((statusConfig) => (
                              <SelectItem key={statusConfig.id} value={String(statusConfig.id)}>
                                {statusConfig.customText || statusConfig.text}
                              </SelectItem>
                            ))
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Default status applied to new bookings (e.g., Planned, Confirmed)
                    </p>
                  </div>

                  {/* Timeline View */}
                  <div className="space-y-2">
                    <Label htmlFor="timeline-view">Timeline View</Label>
                    <Select
                      value={normalizeTimelineView(settings.timelineDefaultView)}
                      onValueChange={handleTimelineViewChange}
                    >
                      <SelectTrigger id="timeline-view">
                        <SelectValue placeholder="Select timeline view" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="customer">Customer</SelectItem>
                        <SelectItem value="project-resource">Project</SelectItem>
                        <SelectItem value="resource">Resource</SelectItem>
                        <SelectItem value="role-resource">Role</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Default view mode for organising the timeline (Customer, Project, Resource, or Role)
                    </p>
                  </div>

                  {/* Timezones - Only show when global timezone feature is enabled */}
                  {settings.enableTimezones && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="timeline-timezone">Timezone Default</Label>
                        {hasUserOverride && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleResetTimezoneToDefault}
                            className="h-7 text-xs"
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Reset to System Default
                          </Button>
                        )}
                      </div>
                      <Select
                        value={effectiveTimezone}
                        onValueChange={handleTimelineTimezoneChange}
                      >
                        <SelectTrigger id="timeline-timezone">
                          <SelectValue placeholder="Select timezone" />
                        </SelectTrigger>
                        <SelectContent>
                          {timezoneOptions
                            .filter((tz) => settings.availableTimezones.includes(tz.value))
                            .map((tz) => (
                            <SelectItem key={tz.value} value={tz.value}>
                              {tz.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">
                          {hasUserOverride ? (
                            <>
                              <span className="font-medium text-primary">Personal override active.</span> Your timezone will be used for timeline and new bookings. System default: {TIMEZONES.find(tz => tz.value === systemDefault)?.label || systemDefault}
                            </>
                          ) : (
                            <>Using system default timezone set by administrator. This will be used for your timeline and new bookings.</>
                          )}
                        </p>
                        {currentMode === 'demo' && (
                          <p className="text-xs text-amber-600 font-medium">
                            ⚠︝ Demo Mode: Changes won't persist. Switch to Live mode in the top-right corner.
                          </p>
                        )}
                        {import.meta.env.DEV && (
                          <p className="text-xs text-muted-foreground font-mono">
                            Debug: effective={effectiveTimezone} | system={systemDefault} | saved={settings.defaultTimezone}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* User Confirm Drag/Drop */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="user-confirm-drag-drop">Confirm Drag/Drop</Label>
                      <p className="text-xs text-muted-foreground">
                        Require confirmation when dragging and dropping bookings (personal preference)
                      </p>
                    </div>
                    <Switch
                      id="user-confirm-drag-drop"
                      checked={settings.userConfirmDragDrop ?? settings.confirmDragDrop}
                      onCheckedChange={handleUserConfirmDragDropToggle}
                    />
                  </div>

                  {/* User Default to Logged In Resource */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="user-default-logged-in">Default to Logged In Resource</Label>
                      <p className="text-xs text-muted-foreground">
                        Automatically select yourself as the default resource (personal preference)
                      </p>
                    </div>
                    <Switch
                      id="user-default-logged-in"
                      checked={settings.userDefaultToLoggedInResource ?? settings.defaultToLoggedInResource}
                      onCheckedChange={handleUserDefaultToLoggedInResourceToggle}
                    />
                  </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

          </CardContent>
        </Card>
      )}



      {/* External Calendar Sync (moved from Integrations page) */}
      {showCalendarSyncSection && (!isAdministrator || activeTab === 'settings') && (
        <Card>
          <CardContent className="space-y-6 pt-6">
            <Collapsible
              open={openSettingsSection === 'calendarSync'}
              onOpenChange={() => handleSettingsSectionToggle('calendarSync')}
            >
              <CollapsibleTrigger className="flex items-center gap-2 mb-4 w-full hover:opacity-70 transition-opacity">
                <Globe className="h-4 w-4" />
                <h3 className="font-semibold">External Calendar Sync</h3>
                {openSettingsSection === 'calendarSync' ? (
                  <ChevronUp className="h-4 w-4 ml-auto" />
                ) : (
                  <ChevronDown className="h-4 w-4 ml-auto" />
                )}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-6">
                  <p className="text-sm text-muted-foreground">
                    Generate a calendar feed URL to sync your scheduler bookings with an external calendar application.
                  </p>

                  {/* Generate + URL row */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Calendar URL</Label>
                    <div className="flex gap-2">
                      <Button
                        className="bg-teal-600 hover:bg-teal-700 text-white shrink-0"
                        onClick={handleGenerateCalendarUrl}
                        disabled={isGeneratingUrl}
                      >
                        {isGeneratingUrl ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Link className="h-4 w-4 mr-2" />
                        )}
                        {isGeneratingUrl ? 'Generating\u2026' : 'Generate URL'}
                      </Button>
                      <Input
                        readOnly
                        value={calendarUrl}
                        placeholder={isLoadingCalendarUrl ? 'Loading…' : 'Please generate URL'}
                        className="flex-1 bg-muted text-muted-foreground text-sm font-mono"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleCopyCalendarUrl}
                        disabled={!calendarUrl}
                        title="Copy URL"
                        className="shrink-0"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>

                    {calendarUrl && (
                      <div className="flex items-center justify-between rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
                        <p className="text-xs text-muted-foreground">
                          Deleting this URL will revoke access. You can generate a new one at any time.
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0 ml-3 gap-1.5"
                          onClick={() => setIsDeleteUrlDialogOpen(true)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete URL
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* How-to instructions */}
                  <div className="space-y-2 rounded-md bg-muted/50 p-4">
                    <p className="text-sm font-medium">How to add to your calendar:</p>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Generate the URL above</li>
                      <li>Copy the URL</li>
                      <li>Open your calendar tool</li>
                      <li>Go to the option "Add Internet/Web Calendar"</li>
                      <li>Paste the copied URL when prompted</li>
                      <li>Save</li>
                    </ol>
                  </div>

                  <p className="text-xs text-muted-foreground border-t pt-3">
                    <strong>Note:</strong> Calendar events will start appearing as new bookings are created on your timeline in Scheduler and on the next refresh cycle of your calendar client, which may be several minutes. Send\Receive in Outlook should start a refresh or you can re-add the calendar to see the latest events immediately.
                  </p>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </CardContent>
        </Card>
      )}



      {/* No Results Message */}
      {searchQuery.trim() && !hasResults && (!isAdministrator || activeTab === 'settings') && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No settings found matching "{searchQuery}"</p>
            <p className="text-sm text-muted-foreground mt-2">Try a different search term</p>
          </CardContent>
        </Card>
      )}

      {/* Delete Calendar URL Confirmation Dialog */}
      <AlertDialog open={isDeleteUrlDialogOpen} onOpenChange={setIsDeleteUrlDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Calendar URL
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete calendar feed url?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCalendarUrl}
              disabled={isDeletingUrl}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingUrl ? 'Deleting…' : 'Yes'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
    </div>
  );
}

interface GroupSetupTabProps {
  currentMode: 'demo' | 'live';
  schedulerGroups: any[];
  resources: any[];
  addSchedulerGroup: (group: any, mode?: 'demo' | 'live') => Promise<void>;
  updateSchedulerGroup: (id: string, updates: any, mode?: 'demo' | 'live') => Promise<void>;
  saveSchedulerGroup: (id: string, mode?: 'demo' | 'live') => Promise<void>;
  deleteSchedulerGroup: (id: string, mode?: 'demo' | 'live') => Promise<void>;
}

function GroupSetupTab({ 
  currentMode, 
  schedulerGroups, 
  resources, 
  addSchedulerGroup, 
  updateSchedulerGroup, 
  saveSchedulerGroup,
  deleteSchedulerGroup 
}: GroupSetupTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [groupNames, setGroupNames] = useState<Record<string, string>>({});
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [openPickerGroupId, setOpenPickerGroupId] = useState<string | null>(null);
  const [resourceSearchQuery, setResourceSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<{ id: string; name: string; resourceCount: number } | null>(null);
  const [groupNameErrors, setGroupNameErrors] = useState<Record<string, string>>({});
  // Track which groups have unsaved changes
  const [dirtyGroupIds, setDirtyGroupIds] = useState<Set<string>>(new Set());
  const hasDirtyGroups = dirtyGroupIds.size > 0;
  // Track known group IDs so we can detect newly added groups
  const knownGroupIdsRef = useRef<Set<string>>(new Set(schedulerGroups.map(g => g.id)));

  useEffect(() => {
    const names: Record<string, string> = {};
    schedulerGroups.forEach(g => { names[g.id] = g.name; });
    setGroupNames(names);

    // Detect newly added groups and mark them dirty
    const currentIds = new Set(schedulerGroups.map(g => g.id));
    currentIds.forEach(id => {
      if (!knownGroupIdsRef.current.has(id)) {
        setDirtyGroupIds(prev => new Set(prev).add(id));
      }
    });
    // Also remove deleted groups from dirty set
    setDirtyGroupIds(prev => {
      const next = new Set(prev);
      prev.forEach(id => { if (!currentIds.has(id)) next.delete(id); });
      return next;
    });
    knownGroupIdsRef.current = currentIds;
  }, [schedulerGroups]);

  const normalizeGroupName = (name: string): string => {
    return name.trim().replace(/\s+/g, ' ').toLowerCase();
  };

  const isGroupNameDuplicate = (name: string, excludeGroupId?: string): boolean => {
    if (!name || !name.trim()) return false;
    
    const normalizedName = normalizeGroupName(name);
    return schedulerGroups.some(g => 
      String(g.id) !== String(excludeGroupId) && 
      normalizeGroupName(g.name) === normalizedName
    );
  };

  const handleAddGroup = async () => {
    const defaultName = 'New Group';
    let finalName = defaultName;
    let counter = 1;

    while (isGroupNameDuplicate(finalName)) {
      finalName = `${defaultName} ${counter}`;
      counter++;
    }

    await addSchedulerGroup({ 
      name: finalName, 
      resourceIds: [],
      avatarUrl: 'https://ui-avatars.com/api/?name=?&background=94a3b8&color=fff&size=40&bold=true'
    }, currentMode);
    // New group detection and dirty marking is handled by the useEffect above
  };

  const handleAvatarUpload = async (groupId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const avatarUrl = reader.result as string;
      await updateSchedulerGroup(groupId, { avatarUrl }, currentMode);
      setDirtyGroupIds(prev => new Set(prev).add(groupId));
    };
    reader.readAsDataURL(file);
  };

  const handleGroupNameChange = (groupId: string, value: string) => {
    if (value.length <= 20) {
      setGroupNames(prev => ({ ...prev, [groupId]: value }));
      // Clear error when user starts editing
      if (groupNameErrors[groupId]) {
        setGroupNameErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[groupId];
          return newErrors;
        });
      }
    }
  };

  const handleGroupNameBlur = async (groupId: string) => {
    const rawName = groupNames[groupId];
    const name = rawName?.trim().replace(/\s+/g, ' ');
    const group = schedulerGroups.find(g => String(g.id) === String(groupId));
    
    if (!name) {
      setGroupNameErrors(prev => ({ ...prev, [groupId]: 'Group name cannot be empty' }));
      setGroupNames(prev => ({ ...prev, [groupId]: group?.name || '' }));
      return;
    }

    if (name.length > 20) {
      setGroupNameErrors(prev => ({ ...prev, [groupId]: 'Group name cannot exceed 20 characters' }));
      setGroupNames(prev => ({ ...prev, [groupId]: group?.name || '' }));
      return;
    }
    
    if (name !== group?.name) {
      if (isGroupNameDuplicate(name, groupId)) {
        setGroupNameErrors(prev => ({ ...prev, [groupId]: 'A group with this name already exists. Please choose a different name.' }));
        setGroupNames(prev => ({ ...prev, [groupId]: group?.name || '' }));
        return;
      }
      
      await updateSchedulerGroup(groupId, { name }, currentMode);
      setDirtyGroupIds(prev => new Set(prev).add(groupId));
    }
  };

  const handleDeleteGroup = (groupId: string) => {
    const group = schedulerGroups.find(g => String(g.id) === String(groupId));
    if (group) {
      setGroupToDelete({
        id: group.id,
        name: group.name,
        resourceCount: group.resourceIds.length
      });
      setDeleteDialogOpen(true);
    }
  };

  const confirmDeleteGroup = async () => {
    if (groupToDelete) {
      await deleteSchedulerGroup(groupToDelete.id, currentMode);
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
      toast.success('Group deleted successfully');
    }
  };

  const cancelDeleteGroup = () => {
    setDeleteDialogOpen(false);
    setGroupToDelete(null);
  };

  const handleRemoveResource = async (groupId: string, resourceId: string) => {
    const group = schedulerGroups.find(g => String(g.id) === String(groupId));
    if (group) {
      const updatedResourceIds = group.resourceIds.filter((id: string) => String(id) !== String(resourceId));
      await updateSchedulerGroup(groupId, { resourceIds: updatedResourceIds }, currentMode);
      setDirtyGroupIds(prev => new Set(prev).add(groupId));
    }
  };

  const handleToggleResource = async (groupId: string, resourceId: string) => {
    const group = schedulerGroups.find(g => String(g.id) === String(groupId));
    if (group) {
      const isSelected = group.resourceIds.includes(resourceId);
      const updatedResourceIds = isSelected
        ? group.resourceIds.filter((id: string) => String(id) !== String(resourceId))
        : [...group.resourceIds, resourceId];
      await updateSchedulerGroup(groupId, { resourceIds: updatedResourceIds }, currentMode);
      setDirtyGroupIds(prev => new Set(prev).add(groupId));
    }
  };

  const availableResources = resources.filter(r => String(r.id) !== 'unallocated');

  const getResourceName = (resourceId: string) => {
    const resource = availableResources.find(r => String(r.id) === String(resourceId));
    return resource?.name || 'Unknown';
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getFilteredPickerResources = () => {
    // Get all resource IDs assigned to OTHER groups (not the current group being edited)
    const assignedToOtherGroups = schedulerGroups
      .filter(g => String(g.id) !== String(openPickerGroupId))
      .flatMap(g => g.resourceIds);

    // Filter out resources that are assigned to other groups
    const unassignedResources = availableResources.filter(
      r => !assignedToOtherGroups.includes(r.id)
    );

    // Apply search filter if query exists
    if (!resourceSearchQuery) return unassignedResources;
    const query = resourceSearchQuery.toLowerCase();
    return unassignedResources.filter(
      (r) => r.name.toLowerCase().includes(query) || r.role.toLowerCase().includes(query)
    );
  };

  const filteredGroups = searchQuery
    ? schedulerGroups.filter(group => {
        const nameMatch = group.name.toLowerCase().includes(searchQuery.toLowerCase());
        const resourceMatch = group.resourceIds.some((id: string) =>
          getResourceName(id).toLowerCase().includes(searchQuery.toLowerCase())
        );
        return nameMatch || resourceMatch;
      })
    : schedulerGroups;

  return (
    <CardContent className="space-y-6 pt-6">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary/10 p-2.5 flex items-center justify-center shrink-0">
            <Users size={22} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Scheduler Group Configuration</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Configure scheduler groups for organizing resources in the calendar view. Resources assigned to groups will be displayed together on the timeline.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            disabled={!hasDirtyGroups || isSavingAll}
            onClick={async () => {
              setIsSavingAll(true);
              try {
                const groupIdsToSave = Array.from(dirtyGroupIds);
                for (const groupId of groupIdsToSave) {
                  await saveSchedulerGroup(groupId, currentMode);
                }
                setDirtyGroupIds(new Set());
              } finally {
                setIsSavingAll(false);
              }
            }}
          >
            {isSavingAll ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save
          </Button>
          <Button
            onClick={handleAddGroup}
          >
            <Plus className="h-4 w-4" />
            Add Group
          </Button>
        </div>
      </div>

      <Separator />

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search groups or resources..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {filteredGroups.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">
              {schedulerGroups.length === 0
                ? 'No groups yet'
                : 'No groups match your search'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {schedulerGroups.length === 0
                ? 'Click "Add Group" to create your first scheduler group.'
                : 'Try adjusting your search query.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredGroups.map((group) => {
            const currentName = groupNames[group.id] ?? group.name;
            return (
              <Card key={group.id} className="relative overflow-hidden">
                <CardContent className="space-y-4 pt-5 pb-5">
                  {/* Delete button */}
                  <div className="absolute top-3 right-3 flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleDeleteGroup(group.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Avatar & Group Name */}
                  <div className="flex items-start gap-4 pr-10">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-muted-foreground">Avatar</Label>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-11 w-11 rounded-lg ring-1 ring-border">
                          <AvatarImage src={group.avatarUrl} alt={group.name} />
                          <AvatarFallback className="rounded-lg bg-primary text-primary-foreground text-base font-semibold">?</AvatarFallback>
                        </Avatar>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/jpeg,image/png,image/gif,image/webp';
                            input.onchange = (event) => handleAvatarUpload(group.id, event as any);
                            input.click();
                          }}
                        >
                          <Upload className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex-1 space-y-1.5">
                      <Label className="text-xs font-medium text-muted-foreground">Group Name</Label>
                      <Input
                        value={currentName}
                        onChange={(e) => handleGroupNameChange(group.id, e.target.value)}
                        onFocus={(e) => {
                          e.target.setSelectionRange(0, e.target.value.length);
                        }}
                        onBlur={() => handleGroupNameBlur(group.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        }}
                        className={cn(
                          'h-9 text-sm',
                          groupNameErrors[group.id] && 'border-destructive focus-visible:ring-destructive/50'
                        )}
                        placeholder="Enter group name"
                        aria-invalid={!!groupNameErrors[group.id]}
                        aria-describedby={groupNameErrors[group.id] ? `error-${group.id}` : undefined}
                      />
                      {groupNameErrors[group.id] ? (
                        <p id={`error-${group.id}`} className="text-xs text-destructive" role="alert">
                          {groupNameErrors[group.id]}
                        </p>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {currentName.length}/20 characters
                        </span>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Assigned Resources */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-muted-foreground">Assigned Resources</Label>
                    <Popover
                      open={openPickerGroupId === group.id}
                      onOpenChange={(open) => {
                        setOpenPickerGroupId(open ? group.id : null);
                        if (!open) setResourceSearchQuery('');
                      }}
                    >
                      <PopoverTrigger asChild>
                        <button className="w-full rounded-lg border border-input min-h-[42px] p-2 flex flex-wrap items-center gap-1.5 cursor-pointer hover:bg-accent/50 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                          {group.resourceIds.length === 0 ? (
                            <span className="text-sm text-muted-foreground px-1 flex items-center gap-2">
                              <User className="h-4 w-4 opacity-50" />
                              Select resources...
                            </span>
                          ) : (
                            group.resourceIds.map((resourceId: string) => (
                              <Badge
                                key={resourceId}
                                className="gap-1 text-sm px-3 py-1"
                                onClose={() => handleRemoveResource(group.id, resourceId)}
                              >
                                {getResourceName(resourceId)}
                              </Badge>
                            ))
                          )}
                          <ChevronsUpDown className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command shouldFilter={false}>
                          <div className="flex items-center border-b px-3">
                            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                            <input
                              placeholder="Search by name or role..."
                              value={resourceSearchQuery}
                              onChange={(e) => setResourceSearchQuery(e.target.value)}
                              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                            />
                          </div>
                          <CommandList className="max-h-[300px]">
                            <CommandEmpty>
                              <div className="py-6 text-center">
                                <div className="rounded-full bg-muted p-3 mx-auto w-fit mb-3">
                                  <User className="h-5 w-5 text-muted-foreground" />
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {resourceSearchQuery 
                                    ? 'No resources match your search' 
                                    : 'All resources are already assigned to groups'}
                                </p>
                                {!resourceSearchQuery && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Remove resources from other groups to make them available
                                  </p>
                                )}
                              </div>
                            </CommandEmpty>
                            <CommandGroup>
                              {getFilteredPickerResources().map((resource) => {
                                const isSelected = group.resourceIds.includes(resource.id);
                                return (
                                  <CommandItem
                                    key={resource.id}
                                    value={resource.id}
                                    onSelect={() => handleToggleResource(group.id, resource.id)}
                                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
                                  >
                                    <div
                                      className={cn(
                                        'flex h-5 w-5 items-center justify-center rounded border border-primary shrink-0',
                                        isSelected
                                          ? 'bg-primary text-primary-foreground'
                                          : 'opacity-50 [&_svg]:invisible'
                                      )}
                                    >
                                      <Check className="h-4 w-4" />
                                    </div>
                                    <Avatar className="h-8 w-8 rounded-md shrink-0">
                                      <AvatarImage src={resource.photoUrl} alt={resource.name} />
                                      <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs rounded-md">
                                        {getInitials(resource.name)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium truncate">{resource.name}</p>
                                      <p className="text-xs text-muted-foreground truncate">{resource.role}</p>
                                    </div>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <span className="text-xs text-muted-foreground">
                      {group.resourceIds.length} resource(s)
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Group Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(isOpen) => !isOpen && cancelDeleteGroup()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the group{' '}
              <span className="font-semibold text-foreground">
                &ldquo;{groupToDelete?.name}&rdquo;
              </span>
              ?{' '}
              {groupToDelete && groupToDelete.resourceCount > 0 && (
                <>
                  This group has{' '}
                  <span className="font-semibold text-foreground">
                    {groupToDelete.resourceCount} resource(s)
                  </span>{' '}
                  assigned that will be unassigned.{' '}
                </>
              )}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDeleteGroup}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteGroup}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CardContent>
  );
}
